<template>
    <div :style="{'max-width': '99%', 'width': '98%', 'margin-left': 'auto', 'margin-right': 'auto', 'margin-top': '10px'}">

        <!-- CSV Upload Modal -->
        <b-modal id="modal-csv-upload" scrollable title="UPLOAD PRODUCTS VIA CSV" size="xl" hide-footer>
            <div class="csv-upload-container">
                <b-alert variant="info" show>
                    <h6>CSV Upload Instructions:</h6>
                    <ul>
                        <li>CSV must contain these 12 columns in order:</li>
                        <li><strong>SKU, DESCRIPTION, CATEGORY, SUB CATEGORY, VENDOR, QTY PER BOX, FOB PRICE, LANDED CST, WHLSL PRICE, SELL PRICE, CUBES, SPIFF</strong></li>
                        <li>First row should be headers</li>
                        <li>SKU must be unique and without spaces</li>
                        <li>SUB CATEGORY must match exactly the subcategory names in the system</li>
                    </ul>
                </b-alert>

                <b-form-file
                    v-model="csvFile"
                    :state="Boolean(csvFile)"
                    placeholder="Choose a CSV file or drop it here..."
                    drop-placeholder="Drop file here..."
                    accept=".csv"
                    @input="onFileSelect"
                ></b-form-file>

                <div v-if="csvPreview.length > 0" class="mt-3">
                    <h6>Preview (First 5 rows):</h6>
                    <b-table striped hover small :items="csvPreview" :fields="csvFields"></b-table>
                </div>

                <div class="mt-3 text-right">
                    <b-button variant="secondary" @click="clearCsvUpload" class="mr-2">Cancel</b-button>
                    <b-button variant="primary" @click="processCsvUpload" :disabled="!csvFile || uploadingCsv">
                        {{ uploadingCsv ? 'Uploading...' : 'Upload Products' }}
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- Low Inventory Alert Modal -->
        <b-modal 
            v-model="showLowInventoryModal" 
            title="Low Inventory Alert Configuration" 
            size="lg"
            @ok="saveLowInventoryAlert"
            @hidden="resetLowInventoryModal"
            centered
        >
            <div v-if="currentAlertProduct" class="low-inventory-form">
                <div class="form-group mb-3">
                    <label class="form-label required">Product</label>
                    <b-input 
                        :value="currentAlertProduct.product_name || currentAlertProduct.sku" 
                        readonly 
                        class="bg-light"
                    ></b-input>
                    <small class="text-muted">SKU: {{ currentAlertProduct.sku }}</small>
                </div>
                
                <div class="form-group mb-3">
                    <label class="form-label required">Alert Type</label>
                    <b-form-radio-group 
                        v-model="alertConfig.alert_type"
                        :options="alertTypeOptions"
                        buttons
                        button-variant="outline-primary"
                        size="sm"
                    ></b-form-radio-group>
                </div>
                
                <div class="form-group mb-3">
                    <label class="form-label required">Threshold Quantity</label>
                    <b-input 
                        v-model.number="alertConfig.threshold_quantity"
                        type="number"
                        min="1"
                        step="1"
                        placeholder="Enter minimum quantity before alert"
                        :state="alertConfig.threshold_quantity > 0 ? null : false"
                    ></b-input>
                    <small class="text-muted">Alert will trigger when inventory falls below this quantity</small>
                </div>
                
                <div class="form-group mb-3">
                    <label class="form-label">Notification Method</label>
                    <b-form-checkbox-group v-model="alertConfig.notification_methods">
                        <b-form-checkbox value="email">Email Alert</b-form-checkbox>
                        <b-form-checkbox value="dashboard">Dashboard Notification</b-form-checkbox>
                        <b-form-checkbox value="sms">SMS Alert</b-form-checkbox>
                    </b-form-checkbox-group>
                </div>
                
                <div class="form-group mb-3" v-if="alertConfig.notification_methods.includes('email')">
                    <label class="form-label">Alert Email(s)</label>
                    <b-input 
                        v-model="alertConfig.alert_emails"
                        placeholder="Enter email addresses (comma separated)"
                    ></b-input>
                    <small class="text-muted">Leave empty to use default admin email</small>
                </div>
                
                <div class="form-group mb-3">
                    <label class="form-label">Additional Notes</label>
                    <b-textarea 
                        v-model="alertConfig.notes"
                        rows="2"
                        placeholder="Optional notes for inventory management"
                    ></b-textarea>
                </div>
                
                <div v-if="currentInventoryLevel !== null" class="alert alert-info">
                    <b-icon-info-circle></b-icon-info-circle>
                    Current inventory level: <strong>{{ currentInventoryLevel }} units</strong>
                    <span v-if="currentInventoryLevel <= alertConfig.threshold_quantity" class="text-danger">
                        - Alert will trigger immediately!
                    </span>
                </div>
            </div>
            
            <div v-else class="text-center py-4">
                <b-icon-exclamation-triangle-fill variant="warning" font-scale="3"></b-icon-exclamation-triangle-fill>
                <h5 class="mt-3">Loading product information...</h5>
            </div>
            
            <template #modal-footer>
                <b-button variant="secondary" @click="showLowInventoryModal = false">Cancel</b-button>
                <b-button variant="warning" @click="saveLowInventoryAlert" :disabled="!isAlertConfigValid || !currentAlertProduct">
                    <b-icon-bell></b-icon-bell> Save Alert
                </b-button>
            </template>
        </b-modal>

        <b-modal 
            id="modal-bulk-status" 
            title="Change Status for Multiple Products" 
            @ok="executeBulkStatusChange"
            @cancel="bulkStatusModalVisible = false"
        >
            <div class="text-center">
                <b-alert show variant="info">
                    <b-icon icon="info-circle" class="mr-2"></b-icon>
                    You are about to change status for <strong>{{ selectedProducts.length }}</strong> product(s)
                </b-alert>
                
                <p class="mt-3">
                    <strong>Action:</strong> 
                    <span v-if="bulkStatusAction === 'active'" class="text-success">ACTIVATE</span>
                    <span v-if="bulkStatusAction === 'notactive'" class="text-danger">DISABLE</span>
                </p>
                
                <b-form-group label="Products to update:" class="mt-3">
                    <div style="max-height: 200px; overflow-y: auto; border: 1px solid #dee2e6; padding: 5px;">
                        <div v-for="sku in selectedProducts" :key="sku" class="text-left">
                            <small>{{ sku }}</small>
                        </div>
                    </div>
                </b-form-group>
                
                <b-form-checkbox v-model="updateChildren" class="mt-3">
                    Also update child products (for parent items)
                </b-form-checkbox>
            </div>
            
            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('modal-bulk-status')">
                    Cancel
                </b-button>
                <b-button variant="primary" @click="executeBulkStatusChange">
                    Confirm {{ bulkStatusAction === 'active' ? 'Activation' : 'Disable' }}
                </b-button>
            </template>
        </b-modal>

        <!-- Bulk Discontinued Modal -->
        <b-modal 
            id="modal-bulk-discontinued" 
            :title="bulkDiscontinuedAction ? 'Mark Products as Discontinued' : 'Remove Discontinued Status'" 
            @ok="executeBulkDiscontinuedChange"
            @hidden="discontinuedModalVisible = false"
            centered
        >
            <div class="text-center">
                <b-alert show :variant="bulkDiscontinuedAction ? 'warning' : 'info'">
                    <b-icon :icon="bulkDiscontinuedAction ? 'archive' : 'arrow-counterclockwise'" class="mr-2"></b-icon>
                    You are about to {{ bulkDiscontinuedAction ? 'mark as discontinued' : 'remove discontinued status' }} for <strong>{{ selectedProducts.length }}</strong> product(s)
                </b-alert>
                
                <div v-if="bulkDiscontinuedAction" class="mt-3">
                    <b-form-group label="Discontinued Reason (Optional)">
                        <b-textarea 
                            v-model="discontinuedReason" 
                            placeholder="Enter reason for discontinuation..."
                            rows="2"
                        ></b-textarea>
                    </b-form-group>
                </div>
                
                <b-form-group label="Products to update:" class="mt-3">
                    <div style="max-height: 200px; overflow-y: auto; border: 1px solid #dee2e6; padding: 5px;">
                        <div v-for="sku in selectedProducts" :key="sku" class="text-left">
                            <small>{{ sku }}</small>
                        </div>
                    </div>
                </b-form-group>
                
                <b-form-checkbox v-model="updateChildrenDiscontinued" class="mt-3">
                    Also update child products (for parent items)
                </b-form-checkbox>
            </div>
            
            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('modal-bulk-discontinued')">
                    Cancel
                </b-button>
                <b-button 
                    :variant="bulkDiscontinuedAction ? 'warning' : 'info'" 
                    @click="executeBulkDiscontinuedChange"
                >
                    {{ bulkDiscontinuedAction ? 'Confirm Discontinue' : 'Remove Discontinued' }}
                </b-button>
            </template>
        </b-modal>

        <!-- Bulk New Arrival Modal -->
        <b-modal 
            id="modal-bulk-new-arrival" 
            :title="bulkNewArrivalAction ? 'Mark Products as New Arrival' : 'Remove New Arrival Status'" 
            @ok="executeBulkNewArrivalChange"
            @hidden="newArrivalModalVisible = false"
            centered
        >
            <div class="text-center">
                <b-alert show :variant="bulkNewArrivalAction ? 'success' : 'info'">
                    <b-icon :icon="bulkNewArrivalAction ? 'star' : 'star-slash'" class="mr-2"></b-icon>
                    You are about to {{ bulkNewArrivalAction ? 'mark as new arrival' : 'remove new arrival status' }} for <strong>{{ selectedProducts.length }}</strong> product(s)
                </b-alert>
                
                <div v-if="bulkNewArrivalAction" class="mt-3">
                    <b-form-group label="New Arrival Duration (Days)">
                        <b-form-input 
                            v-model.number="newArrivalDays" 
                            type="number" 
                            min="1" 
                            max="365"
                            class="text-center"
                        ></b-form-input>
                        <small class="text-muted">Products will be marked as new arrivals for {{ newArrivalDays }} days</small>
                    </b-form-group>
                </div>
                
                <b-form-group label="Products to update:" class="mt-3">
                    <div style="max-height: 200px; overflow-y: auto; border: 1px solid #dee2e6; padding: 5px;">
                        <div v-for="sku in selectedProducts" :key="sku" class="text-left">
                            <small>{{ sku }}</small>
                        </div>
                    </div>
                </b-form-group>
                
                <b-form-checkbox v-model="updateChildrenNewArrival" class="mt-3">
                    Also update child products (for parent items)
                </b-form-checkbox>
            </div>
            
            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('modal-bulk-new-arrival')">
                    Cancel
                </b-button>
                <b-button 
                    :variant="bulkNewArrivalAction ? 'success' : 'info'" 
                    @click="executeBulkNewArrivalChange"
                >
                    {{ bulkNewArrivalAction ? 'Confirm New Arrival' : 'Remove New Arrival' }}
                </b-button>
            </template>
        </b-modal>

        <!-- Barcode Viewer Modal -->
        <b-modal id="modal-barcode-viewer" size="md" hide-footer centered title="Product Barcode">
            <div class="text-center">
                <b-img
                    :src="currentBarcodeImage"
                    fluid
                    alt="Product barcode"
                    style="max-height: 70vh; object-fit: contain;"
                />
                <div class="mt-3">
                    <strong>{{ currentProductName }}</strong>
                    <div class="small text-muted">{{ currentProductSku }}</div>
                </div>
                <div class="mt-2">
                    <b-button 
                        size="sm" 
                        variant="outline-primary"
                        @click="downloadBarcodeImage(currentBarcodeImage, currentProductSku)"
                    >
                        <b-icon icon="download" aria-hidden="true"></b-icon>
                        Download Barcode
                    </b-button>
                </div>
            </div>
            <template #modal-footer>
                <b-button size="sm" variant="secondary" @click="$bvModal.hide('modal-barcode-viewer')">Close</b-button>
            </template>
        </b-modal>

        <!-- Barcode Progress Modal -->
        <b-modal 
            id="modal-barcode-progress" 
            title="Barcode Generation Progress" 
            size="lg"
            :hide-footer="true"
            :no-close-on-backdrop="true"
            :no-close-on-esc="true"
            :busy="generatingBarcodes"
        >
            <div class="text-center">
                <b-progress 
                    :value="barcodeProgress.percentage" 
                    :max="100"
                    variant="success"
                    animated
                    class="mb-3"
                    :show-value="true"
                ></b-progress>
                
                <div class="mb-2">
                    <strong>Status:</strong> {{ barcodeProgress.message }}
                </div>
                
                <div class="row">
                    <div class="col-6">
                        <div class="border rounded p-2">
                            <small class="text-muted">Processed</small>
                            <h5>{{ barcodeProgress.processed }} / {{ barcodeProgress.total }}</h5>
                        </div>
                    </div>
                    <div class="col-6">
                        <div class="border rounded p-2">
                            <small class="text-muted">Results</small>
                            <h5>
                                <span class="text-success">{{ barcodeProgress.successful }}</span> / 
                                <span class="text-danger">{{ barcodeProgress.failed }}</span>
                            </h5>
                        </div>
                    </div>
                </div>
                
                <div v-if="barcodeProgress.errors.length > 0" class="mt-3">
                    <b-alert variant="danger" show>
                        <strong>Errors encountered:</strong>
                        <div class="mt-2" style="max-height: 200px; overflow-y: auto;">
                            <div v-for="(error, idx) in barcodeProgress.errors.slice(0, 5)" :key="idx" class="small">
                                {{ error.sku }}: {{ error.error }}
                            </div>
                            <div v-if="barcodeProgress.errors.length > 5" class="small text-muted">
                                ... and {{ barcodeProgress.errors.length - 5 }} more errors
                            </div>
                        </div>
                    </b-alert>
                </div>
                
                <div class="mt-3">
                    <b-spinner v-if="generatingBarcodes" small class="mr-2"></b-spinner>
                    <b-button 
                        v-if="!generatingBarcodes" 
                        variant="primary" 
                        @click="$bvModal.hide('modal-barcode-progress')"
                    >
                        Close
                    </b-button>
                </div>
            </div>
        </b-modal>
        
        <!-- Image Viewer Modal -->
        <b-modal id="modal-image-viewer" size="lg" hide-footer centered title="Product Image">
            <div class="text-center">
                <b-img
                    :src="currentFullSizeImage"
                    fluid
                    alt="Full size product image"
                    style="max-height: 70vh; object-fit: contain;"
                />
                <div class="mt-3">
                    <strong>{{ currentProductName }}</strong>
                </div>
            </div>
            <template #modal-footer>
                <b-button size="sm" variant="secondary" @click="$bvModal.hide('modal-image-viewer')">Close</b-button>
            </template>
        </b-modal>

        <!-- Product Stats Modal -->
        <b-modal id="modal-product-stats" scrollable title="PRODUCT STATS" size="xl" hide-footer style="min-height: 700px;">
            <ProductStatsModal
                v-on:done-product-stats="close_item_stats"
                v-on:changed-product="monitor_modal_change"
                style="min-height: 500px;"
                :viewdata="stats_item"
            />
        </b-modal>

        <!-- ETA Details Modal -->
        <b-modal id="modal-eta-details" scrollable :title="`ETA DETAILS - ${currentProduct ? currentProduct.product_name : ''} (${currentProduct ? currentProduct.sku : ''})`" size="lg" hide-footer>
            <div class="details-container">
                <b-table striped hover :items="etaDetails" :fields="detailFields">
                    <template #cell(value)="data">
                        <b-badge variant="primary">{{ data.value }}</b-badge>
                    </template>
                </b-table>
            </div>
        </b-modal>

        <!-- Sold Details Modal -->
        <b-modal id="modal-sold-details" scrollable :title="`SOLD DETAILS - ${currentProduct ? currentProduct.product_name : ''} (${currentProduct ? currentProduct.sku : ''})`" size="lg" hide-footer>
            <div class="details-container">
                <b-table striped hover :items="soldDetails" :fields="detailFields">
                    <template #cell(value)="data">
                        <b-badge variant="success">{{ data.value }}</b-badge>
                    </template>
                </b-table>
            </div>
        </b-modal>

        <!-- Received Details Modal -->
        <b-modal id="modal-received-details" scrollable :title="`RECEIVED DETAILS - ${currentProduct ? currentProduct.product_name : ''} (${currentProduct ? currentProduct.sku : ''})`" size="lg" hide-footer>
            <div class="details-container">
                <b-table striped hover :items="receivedDetails" :fields="detailFields">
                    <template #cell(value)="data">
                        <b-badge variant="info">{{ data.value }}</b-badge>
                    </template>
                </b-table>
            </div>
        </b-modal>

        <!-- Scheduled Details Modal -->
        <b-modal id="modal-scheduled-details" scrollable :title="`SCHEDULED DETAILS - ${currentProduct ? currentProduct.product_name : ''} (${currentProduct ? currentProduct.sku : ''})`" size="lg" hide-footer>
            <div class="details-container">
                <b-table striped hover :items="scheduledDetails" :fields="detailFields">
                    <template #cell(value)="data">
                        <b-badge variant="warning">{{ data.value }}</b-badge>
                    </template>
                </b-table>
            </div>
        </b-modal>

        <!-- Bulk Image Upload Modal -->
        <b-modal 
            id="modal-bulk-image-upload" 
            title="BULK IMAGE UPLOAD" 
            size="xl"
            @hidden="resetBulkImageUpload"
            hide-footer
        >
            <div class="bulk-image-upload-container">
                <b-alert variant="info" show>
                    <h6>Bulk Image Upload Instructions:</h6>
                    <ul>
                        <li>Select multiple image files (JPG, PNG, GIF, WebP)</li>
                        <li><strong>Each filename MUST match the product SKU exactly</strong></li>
                        <li>Example: <code>SKU12345.jpg</code> will be uploaded to product with SKU "SKU12345"</li>
                        <li>Supported formats: JPG, PNG, GIF, WebP. Max size per image: 5MB</li>
                        <li>Images will be processed in sequence</li>
                    </ul>
                </b-alert>
                
                <b-form-file
                    v-model="bulkImages"
                    :state="Boolean(bulkImages.length)"
                    placeholder="Choose image files or drop them here..."
                    drop-placeholder="Drop images here..."
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    multiple
                    @input="handleBulkImageSelect"
                ></b-form-file>
                
                <div v-if="bulkImagePreview.length > 0" class="mt-3">
                    <h6>Selected Images ({{ bulkImagePreview.length }}):</h6>
                    <div class="image-preview-grid">
                        <div v-for="(img, idx) in bulkImagePreview" :key="idx" class="preview-item">
                            <img :src="img.preview" :alt="img.sku" class="preview-thumb" />
                            <div class="preview-info">
                                <strong>{{ img.sku }}</strong>
                                <small>{{ img.fileName }}</small>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="mt-3 text-right">
                    <b-button variant="secondary" @click="$bvModal.hide('modal-bulk-image-upload')" class="mr-2">Cancel</b-button>
                    <b-button 
                        variant="primary" 
                        @click="processBulkImageUpload" 
                        :disabled="bulkImages.length === 0 || uploadingBulkImages"
                    >
                        {{ uploadingBulkImages ? 'Uploading...' : `Upload ${bulkImages.length} Images` }}
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- Bulk Image Progress Modal -->
        <b-modal 
            id="modal-bulk-image-progress" 
            title="Bulk Image Upload Progress" 
            size="lg"
            :hide-footer="true"
            :no-close-on-backdrop="true"
            :no-close-on-esc="true"
            :busy="uploadingBulkImages"
        >
            <div class="text-center">
                <b-progress 
                    :value="(bulkImageProgress.processed / bulkImageProgress.total) * 100" 
                    :max="100"
                    variant="success"
                    animated
                    class="mb-3"
                    :show-value="true"
                ></b-progress>
                
                <div class="mb-2">
                    <strong>Status:</strong> Processing {{ bulkImageProgress.current }} of {{ bulkImageProgress.total }}
                </div>
                
                <div class="row">
                    <div class="col-4">
                        <div class="border rounded p-2">
                            <small class="text-muted">Total</small>
                            <h5>{{ bulkImageProgress.total }}</h5>
                        </div>
                    </div>
                    <div class="col-4">
                        <div class="border rounded p-2">
                            <small class="text-muted">Successful</small>
                            <h5 class="text-success">{{ bulkImageProgress.successful }}</h5>
                        </div>
                    </div>
                    <div class="col-4">
                        <div class="border rounded p-2">
                            <small class="text-muted">Failed</small>
                            <h5 class="text-danger">{{ bulkImageProgress.failed }}</h5>
                        </div>
                    </div>
                </div>
                
                <div v-if="bulkImages.filter(i => i.status === 'failed').length > 0" class="mt-3">
                    <b-alert variant="danger" show>
                        <strong>Failed uploads:</strong>
                        <div class="mt-2" style="max-height: 200px; overflow-y: auto;">
                            <div v-for="(img, idx) in bulkImages.filter(i => i.status === 'failed')" :key="idx" class="small">
                                <strong>{{ img.sku }}</strong>: {{ img.error }}
                            </div>
                        </div>
                    </b-alert>
                </div>
                
                <div class="mt-3">
                    <b-spinner v-if="uploadingBulkImages" small class="mr-2"></b-spinner>
                    <b-button 
                        v-if="!uploadingBulkImages" 
                        variant="primary" 
                        @click="$bvModal.hide('modal-bulk-image-progress')"
                    >
                        Close
                    </b-button>
                </div>
            </div>
        </b-modal>
        
        <!-- Image Upload Modal -->
        <b-modal 
            id="modal-image-upload" 
            size="md" 
            hide-footer 
            centered 
            :title="`Upload Image - ${uploadProduct ? uploadProduct.sku : ''}`"
            @hidden="resetImageUpload"
        >
            <div class="image-upload-container">
                <div v-if="!imagePreview" class="upload-area" @click="triggerFileInput">
                    <div class="upload-placeholder">
                        <b-icon icon="cloud-upload" font-scale="3"></b-icon>
                        <div class="mt-2">Click to select an image</div>
                        <small class="text-muted">or drag and drop</small>
                    </div>
                </div>
                
                <div v-else class="image-preview-container">
                    <img :src="imagePreview" alt="Preview" class="preview-image" />
                    <div class="preview-actions mt-2">
                        <b-button size="sm" variant="secondary" @click="clearImage">Remove</b-button>
                    </div>
                </div>
                
                <input
                    ref="fileInput"
                    type="file"
                    accept="image/*"
                    style="display: none"
                    @change="handleFileSelect"
                />
                
                <div class="upload-info mt-3">
                    <small class="text-muted">Supported formats: JPG, PNG, GIF, WebP. Max size: 5MB</small>
                </div>
                
                <div class="mt-4 text-right">
                    <b-button variant="secondary" @click="$bvModal.hide('modal-image-upload')" class="mr-2">Cancel</b-button>
                    <b-button variant="primary" @click="uploadProductImage" :disabled="!selectedFile || uploadingImage">
                        {{ uploadingImage ? 'Uploading...' : 'Upload Image' }}
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- Bulk Status Change Modal -->
        <b-modal 
            id="modal-bulk-status" 
            title="Change Status for Multiple Products" 
            @ok="executeBulkStatusChange"
            @cancel="bulkStatusModalVisible = false"
        >
            <div class="text-center">
                <b-alert show variant="info">
                    <b-icon icon="info-circle" class="mr-2"></b-icon>
                    You are about to change status for <strong>{{ selectedProducts.length }}</strong> product(s)
                </b-alert>
                
                <p class="mt-3">
                    <strong>Action:</strong> 
                    <span v-if="bulkStatusAction === 'active'" class="text-success">ACTIVATE</span>
                    <span v-if="bulkStatusAction === 'notactive'" class="text-danger">DISABLE</span>
                </p>
                
                <b-form-group label="Products to update:" class="mt-3">
                    <div style="max-height: 200px; overflow-y: auto; border: 1px solid #dee2e6; padding: 5px;">
                        <div v-for="sku in selectedProducts" :key="sku" class="text-left">
                            <small>{{ sku }}</small>
                        </div>
                    </div>
                </b-form-group>
                
                <b-form-checkbox v-model="updateChildren" class="mt-3">
                    Also update child products (for parent items)
                </b-form-checkbox>
            </div>
        </b-modal>

        <!-- ============================================ -->
        <!-- PRINT BARCODES MODAL - ADD THIS -->
        <!-- ============================================ -->
        <b-modal 
            id="modal-print-barcodes" 
            title="PRINT BARCODE LABELS (1.5\\ x 2\\)" 
            size="xl" 
            centered 
            hide-footer 
            @hidden="resetPrintModal"
        >
            <div v-if="printProducts.length > 0">
                <b-alert :variant="printProducts.length > 24 ? 'danger' : 'info'" show>
                    <div class="d-flex justify-content-between align-items-center">
                        <span>
                            <strong>{{ printProducts.length }}</strong> of <strong>24</strong> products selected for printing
                        </span>
                        <b-button size="sm" variant="outline-danger" @click="clearPrintAll">Clear All</b-button>
                    </div>
                </b-alert>

                <!-- Print Preview Grid -->
                <div class="print-grid">
                    <div v-for="(p, idx) in printProducts.slice(0,24)" :key="p.sku" class="print-card">
                        <div class="print-card-header">
                            <span class="print-number">{{ idx + 1 }}</span>
                            <b-button size="sm" variant="link" class="p-0 text-danger" @click="removePrintItem(idx)">✕</b-button>
                        </div>
                        <img :src="p.barcodeDataUrl" class="print-barcode-preview" />
                        <div class="print-card-text">
                            <div class="print-sku"><strong>{{ p.sku }}</strong></div>
                            <div class="print-name">{{ truncateText(p.product_name, 20) }}</div>
                            <div v-if="printSettings.showPrice" class="print-price">${{ formatPriceNum(p.selling_price) }}</div>
                        </div>
                    </div>
                </div>

                <!-- Print Settings -->
                <b-row class="mt-3">
                    <b-col md="3">
                        <b-form-group label="Label Size">
                            <b-form-select v-model="printSettings.labelSize" :options="labelSizeOptions"></b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col md="3">
                        <b-form-group label="Copies">
                            <b-form-input type="number" v-model="printSettings.copies" min="1" max="10"></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col md="3">
                        <b-form-group label="Print Method">
                            <b-form-select v-model="printSettings.printMethod" :options="printMethodOptions"></b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col md="3">
                        <b-form-group label="Layout">
                            <b-form-select v-model="printSettings.layout" :options="layoutOptions"></b-form-select>
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row class="mt-2">
                    <b-col md="12">
                        <b-form-checkbox v-model="printSettings.showPrice" class="mr-3">Show Price</b-form-checkbox>
                        <b-form-checkbox v-model="printSettings.showProductName" class="mr-3">Show Product Name</b-form-checkbox>
                        <b-form-checkbox v-model="printSettings.showSku">Show SKU</b-form-checkbox>
                    </b-col>
                </b-row>

                <!-- Action Buttons -->
                <div class="mt-4 text-right">
                    <b-button variant="secondary" @click="$bvModal.hide('modal-print-barcodes')" class="mr-2">Cancel</b-button>
                    <b-button 
                        variant="primary" 
                        @click="executePrint" 
                        :disabled="printProducts.length === 0 || printProducts.length > 24 || isPrinting"
                    >
                        <b-spinner v-if="isPrinting" small class="mr-2"></b-spinner>
                        {{ isPrinting ? 'Printing...' : `Print ${printProducts.length} Label(s)` }}
                    </b-button>
                </div>
            </div>
            <div v-else class="text-center py-5">
                <b-icon icon="inbox" font-scale="3" variant="secondary"></b-icon>
                <h5 class="mt-3">No products selected</h5>
                <p>Please select products from the list first (max 24)</p>
            </div>
        </b-modal>

        <!-- Main Content -->
        <div v-if="items_being_loaded == true">
            <loading :active="items_being_loaded" :can-cancel="false" :is-full-page="true"></loading>
        </div>

        <div v-else>
            <!-- COMPACT TOOLBAR - ICONS WITH HOVER LABELS -->
            <div class="compact-toolbar">
                <div class="toolbar-group">
                    <b-button 
                        variant="outline-success" 
                        @click="fetch_init(false, true)"
                        v-b-tooltip.hover.top="'Fetch Products'"
                        size="sm"
                    >
                        <b-icon icon="arrow-repeat"></b-icon>
                    </b-button>

                    <b-dropdown 
                        v-if="packet.userDetails.userrole === 'admin'" 
                        variant="outline-primary" 
                        :disabled="downloadingExcel"
                        v-b-tooltip.hover.top="'Download CSV'"
                        size="sm"
                        no-caret
                        toggle-class="text-decoration-none"
                    >
                        <template #button-content>
                            <b-icon icon="download"></b-icon>
                        </template>
                        <b-dropdown-item @click="downloadCSV(false)">Download Current View ({{ filtered_final.length }} items)</b-dropdown-item>
                        <b-dropdown-item @click="downloadCSV(true)">Download All Filtered Data</b-dropdown-item>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item @click="downloadByCategory" v-if="category_id">Download Entire Category</b-dropdown-item>
                        <b-dropdown-item @click="downloadByVendor" v-if="vendor_id">Download Entire Vendor</b-dropdown-item>
                    </b-dropdown>

                    <b-button 
                        v-if="packet.userDetails.userrole === 'admin'" 
                        variant="outline-info" 
                        @click="showCsvUpload"
                        v-b-tooltip.hover.top="'Upload CSV'"
                        size="sm"
                    >
                        <b-icon icon="cloud-upload"></b-icon>
                    </b-button>

                    <b-button 
                        v-if="packet.userDetails.userrole === 'admin'" 
                        variant="outline-info" 
                        @click="showBulkImageUpload"
                        v-b-tooltip.hover.top="'Bulk Image Upload'"
                        size="sm"
                    >
                        <b-icon icon="images"></b-icon>
                    </b-button>

                    <b-button 
                        v-if="packet.userDetails.userrole === 'admin'" 
                        variant="outline-warning" 
                        @click="generateAllBarcodes" 
                        :disabled="generatingBarcodes"
                        v-b-tooltip.hover.top="'Generate Barcodes for All SKUs'"
                        size="sm"
                    >
                        <b-icon icon="upc-scan"></b-icon>
                    </b-button>

                    <b-button 
                        v-if="packet.userDetails.userrole === 'admin'" 
                        variant="outline-success" 
                        @click="openPrintModal"
                        :disabled="selectedProducts.length === 0 || selectedProducts.length > 24"
                        v-b-tooltip.hover.top="'Print Barcodes'"
                        size="sm"
                    >
                        <b-icon icon="printer"></b-icon>
                        <b-badge v-if="selectedProducts.length > 0" variant="primary" class="ml-1">{{ selectedProducts.length }}</b-badge>
                    </b-button>

                    <b-dropdown 
                        v-if="showSelectionMode && selectedProducts.length > 0" 
                        variant="primary" 
                        :text="'Bulk Actions (' + selectedProducts.length + ')'"
                        size="sm"
                        class="ml-2"
                    >
                        <template #button-content>
                            <b-icon icon="gear"></b-icon>
                            <span class="toolbar-label">Bulk Actions ({{ selectedProducts.length }})</span>
                        </template>
                        <b-dropdown-item @click="openBulkStatusModal('active')">
                            <b-icon icon="play-btn" variant="success"></b-icon> Activate Selected
                        </b-dropdown-item>
                        <b-dropdown-item @click="openBulkStatusModal('notactive')">
                            <b-icon icon="pause-btn" variant="danger"></b-icon> Disable Selected
                        </b-dropdown-item>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item @click="openBulkDiscontinuedModal(true)">
                            <b-icon icon="archive" variant="warning"></b-icon> Mark as Discontinued
                        </b-dropdown-item>
                        <b-dropdown-item @click="openBulkDiscontinuedModal(false)">
                            <b-icon icon="arrow-counterclockwise" variant="info"></b-icon> Remove Discontinued
                        </b-dropdown-item>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item @click="openBulkNewArrivalModal(true)">
                            <b-icon icon="star" variant="success"></b-icon> Mark as New Arrival
                        </b-dropdown-item>
                        <b-dropdown-item @click="openBulkNewArrivalModal(false)">
                            <b-icon icon="star-slash" variant="secondary"></b-icon> Remove New Arrival
                        </b-dropdown-item>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item @click="clearSelection">
                            <b-icon icon="x-circle"></b-icon> Clear Selection
                        </b-dropdown-item>
                    </b-dropdown>

                    <b-badge 
                        v-if="showSelectionMode" 
                        variant="info" 
                        class="ml-2"
                    >
                        <b-icon icon="check2-square"></b-icon>
                        {{ selectedProducts.length }} product(s) selected
                    </b-badge>  

                    <b-button 
                        v-if="packet.userDetails.userrole === 'admin'" 
                        :variant="showSelectionMode ? 'primary' : 'outline-secondary'" 
                        @click="toggleSelectionMode"
                        v-b-tooltip.hover.top="'Select Products'"
                        size="sm"
                    >
                        <b-icon :icon="showSelectionMode ? 'check2-square' : 'square'"></b-icon>
                        <b-badge v-if="showSelectionMode && selectedProducts.length > 0" variant="light" class="ml-1">{{ selectedProducts.length }}</b-badge>
                    </b-button>
                </div>

                <!-- Column Visibility Group -->
                <div class="toolbar-group">
                    <b-button 
                        :variant="Showimage ? 'primary' : 'outline-secondary'" 
                        @click="toggleimage"
                        v-b-tooltip.hover.top="'Show/Hide Image Column'"
                        size="sm"
                    >
                        <b-icon :icon="Showimage ? 'eye' : 'eye-slash'"></b-icon>
                        <span class="toolbar-label">IMAGE</span>
                    </b-button>

                    <b-button 
                        :variant="ShowBarcode ? 'primary' : 'outline-secondary'" 
                        @click="toggleBarcode"
                        v-b-tooltip.hover.top="'Show/Hide Barcode Column'"
                        size="sm"
                    >
                        <b-icon :icon="ShowBarcode ? 'eye' : 'eye-slash'"></b-icon>
                        <span class="toolbar-label">BARCODE</span>
                    </b-button>

                    <b-button 
                        :variant="showMainColumns ? 'primary' : 'outline-secondary'" 
                        @click="toggleMainColumns"
                        v-b-tooltip.hover.top="'Show/Hide Main Columns'"
                        size="sm"
                    >
                        <b-icon :icon="showMainColumns ? 'eye' : 'eye-slash'"></b-icon>
                        <span class="toolbar-label">MAIN</span>
                    </b-button>

                    <b-button 
                        :variant="showPriceColumns ? 'primary' : 'outline-secondary'" 
                        @click="togglePriceColumns"
                        v-b-tooltip.hover.top="'Show/Hide Price Columns'"
                        size="sm"
                    >
                        <b-icon :icon="showPriceColumns ? 'eye' : 'eye-slash'"></b-icon>
                        <span class="toolbar-label">PRICE</span>
                    </b-button>

                    <b-button 
                        :variant="showDiscountColumns ? 'primary' : 'outline-secondary'" 
                        @click="toggleDiscountColumns"
                        v-b-tooltip.hover.top="'Show/Hide Discount Columns'"
                        size="sm"
                    >
                        <b-icon :icon="showDiscountColumns ? 'eye' : 'eye-slash'"></b-icon>
                        <span class="toolbar-label">DISCOUNT</span>
                    </b-button>

                    <b-button 
                        :variant="showDetailColumns ? 'primary' : 'outline-secondary'" 
                        @click="toggleDetailColumns"
                        v-b-tooltip.hover.top="'Show/Hide Detail Columns'"
                        size="sm"
                    >
                        <b-icon :icon="showDetailColumns ? 'eye' : 'eye-slash'"></b-icon>
                        <span class="toolbar-label">DETAILS</span>
                    </b-button>
                </div>
            </div>

            <!-- Mobile Filters Toggle -->
            <div v-if="isMobile" class="mobile-filters-toggle mb-2">
                <b-button size="sm" variant="outline-primary" @click="filtersVisible = !filtersVisible" block>
                    <b-icon-funnel-fill class="mr-1" />
                    {{ filtersVisible ? "Hide Filters" : "Show Filters" }}
                    <b-badge v-if="activeFiltersCount > 0" variant="primary" class="ml-1">{{ activeFiltersCount }}</b-badge>
                </b-button>
            </div>

            <!-- Filters -->
            <b-collapse :visible="!isMobile || filtersVisible" style="width: 100%;">
                <b-card class="filter-card" no-body>
                    <!-- Primary Filters -->
                    <div class="filter-primary p-3">
                        <b-row class="align-items-end g-2">
                            <b-col md="2" class="mb-2">
                                <label class="filter-label">VENDOR</label>
                                <b-form-select :options="packet.vendors" v-model="vendor_id" v-on:change="fetch_vendor" size="sm" class="filter-select"></b-form-select>
                            </b-col>
                            
                            <b-col md="2" class="mb-2">
                                <label class="filter-label">CATEGORY</label>
                                <b-form-select :options="packet.categories" v-model="category_id" v-on:change="fetch_category" size="sm" class="filter-select"></b-form-select>
                            </b-col>
                            
                            <b-col md="2" class="mb-2">
                                <label class="filter-label">SUB CATEGORY</label>
                                <b-form-select :options="subcategoryOptions" v-model="subcategory_id" @change="fetchSubcategory" size="sm" class="filter-select" :disabled="!category_id"></b-form-select>
                            </b-col>
                            
                            <b-col md="3" class="mb-2">
                                <label class="filter-label">SEARCH</label>
                                <b-input-group size="sm" class="filter-search">
                                    <b-form-input v-model="product_query" @keyup.enter="fetch_product_name_sku" placeholder="Name or SKU..." class="filter-input"></b-form-input>
                                    <b-input-group-append>
                                        <b-button variant="outline-secondary" @click="fetch_product_name_sku" size="sm">
                                            <b-icon icon="search"></b-icon>
                                        </b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-col>
                            
                            <b-col v-if="packet.userDetails.userrole === 'admin'" md="2" class="mb-2">
                                <label class="filter-label">LOCATION</label>
                                <b-form-select :options="packet.locations" v-model="location_id" v-on:change="fetch_location" size="sm" class="filter-select"></b-form-select>
                            </b-col>
                            
                            <b-col md="1" class="mb-2 text-right">
                                <b-button 
                                    variant="link" 
                                    size="sm" 
                                    @click="showMoreFilters = !showMoreFilters" 
                                    class="filter-toggle-btn p-0"
                                >
                                    <b-icon :icon="showMoreFilters ? 'chevron-up' : 'chevron-down'"></b-icon>
                                    {{ showMoreFilters ? 'Less' : 'More' }}
                                </b-button>
                            </b-col>
                        </b-row>
                    </div>

                    <!-- More Filters -->
                    <b-collapse v-model="showMoreFilters">
                        <div class="filter-more p-3 border-top">
                            <b-row class="align-items-end g-2">
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">ITEM TYPE</label>
                                    <b-form-select :options="itemTypeOptions" v-model="itemTypeFilter" @change="applyFilters" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">ITEM STATUS</label>
                                    <b-form-select :options="itemStatusOptions" v-model="itemStatusFilter" @change="applyItemStatusFilter" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">PRODUCT STATUS</label>
                                    <b-form-select :options="statusOptions" v-model="statusFilter" v-on:change="applyStatusFilter" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">DISCONTINUED</label>
                                    <b-form-select :options="discontinuedOptions" v-model="discontinuedFilter" @change="applyDiscontinuedFilter" size="sm" class="filter-select"></b-form-select>
                                </b-col>

                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">NEW ARRIVAL</label>
                                    <b-form-select :options="newArrivalOptions" v-model="newArrivalFilter" @change="applyNewArrivalFilter" size="sm" class="filter-select"></b-form-select>
                                </b-col>

                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">RECEIVED STATUS</label>
                                    <b-form-select :options="receivedStatusOptions" v-model="receivedStatusFilter" @change="applyReceivedStatusFilter" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">TYPE</label>
                                    <b-form-select :options="typeOptions" v-model="typeFilter" @change="applyDetailFilters" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">COLOR</label>
                                    <b-form-select :options="colorOptions" v-model="colorFilter" @change="applyDetailFilters" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">SIZE</label>
                                    <b-form-select :options="sizeOptions" v-model="sizeFilter" @change="applyDetailFilters" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                            </b-row>

                            <b-row class="align-items-end g-2 mt-2">
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">MATERIAL</label>
                                    <b-form-select :options="materialOptions" v-model="materialFilter" @change="applyDetailFilters" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">FUNCTIONALITY</label>
                                    <b-form-select :options="functionalityOptions" v-model="functionalityFilter" @change="applyDetailFilters" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">SPIFF</label>
                                    <b-form-select :options="spiffFilterOptions" v-model="spiffFilter" @change="applySpiffFilter" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                
                                <b-col md="3" class="mb-2">
                                    <label class="filter-label">BEST SELLING</label>
                                    <div class="d-flex align-items-center">
                                        <b-form-select :options="bestSellingOptions" v-model="bestSellingFilter" @change="applyBestSellingFilter" size="sm" class="filter-select flex-grow-1"></b-form-select>
                                        <b-badge v-if="bestSellingFilter" variant="warning" class="ml-2 filter-badge">Essential</b-badge>
                                    </div>
                                </b-col>
                                
                                <b-col md="3" class="mb-2">
                                    <label class="filter-label">SLOWEST SELLING</label>
                                    <div class="d-flex align-items-center">
                                        <b-form-select :options="slowestSellingOptions" v-model="slowestSellingFilter" @change="applySlowestSellingFilter" size="sm" class="filter-select flex-grow-1"></b-form-select>
                                        <b-badge v-if="slowestSellingFilter" variant="danger" class="ml-2 filter-badge">Essential</b-badge>
                                    </div>
                                </b-col>
                                
                                <b-col md="2" class="mb-2 text-right">
                                    <b-button variant="outline-secondary" size="sm" @click="resetAllFilters" class="reset-filters-btn">
                                        <b-icon icon="arrow-repeat"></b-icon>
                                        Reset
                                    </b-button>
                                </b-col>
                            </b-row>
                        </div>
                    </b-collapse>
                    
                    <!-- Active Filters Display -->
                    <div v-if="activeFiltersCount > 0" class="filter-active-badges p-2 border-top bg-light">
                        <small class="text-muted mr-2">Active filters:</small>
                        <b-badge v-for="filter in getActiveFiltersList" :key="filter.key" variant="info" class="mr-1 mb-1 filter-badge-active">
                            {{ filter.label }}: {{ filter.value }}
                            <b-icon icon="x" size="xs" class="ml-1" style="cursor: pointer;" @click="removeFilter(filter.key)"></b-icon>
                        </b-badge>
                        <b-button variant="link" size="sm" class="p-0 ml-2" @click="clearAllFilters">Clear all</b-button>
                    </div>

                    <!-- ============================================ -->
                    <!-- NEW: Stats Button - Outside More section, next to Reset -->
                    <!-- ============================================ -->
                    <div class="filter-stats-actions p-2 border-top bg-light" v-if="showStatsButton">
                        <b-row class="align-items-center">
                            <b-col md="12" class="text-right">
                                <b-button 
                                    variant="info" 
                                    size="sm" 
                                    @click="fetchCategoryStockStats" 
                                    :disabled="isLoadingCategoryStats"
                                    class="px-4"
                                >
                                    <b-icon icon="bar-chart-fill" class="mr-2"></b-icon>
                                    <span v-if="isLoadingCategoryStats">
                                        <b-spinner small class="mr-1"></b-spinner>Loading...
                                    </span>
                                    <span v-else>Show Stats for Stock</span>
                                </b-button>
                            </b-col>
                        </b-row>
                    </div>
                </b-card>
            </b-collapse>

          
          <!-- Products Table -->
<div v-if="displayedItems.length > 0" class="diver mt-2" @scroll="handleScroll">
    <table style="width: 100%;">
        <thead>
            <tr>
                <th v-if="showSelectionMode" class="srr sticky-col" style="min-width: 40px; width: 40px; max-width: 40px; background:lightgoldenrodyellow; z-index: 5;">
                    <b-form-checkbox v-model="selectAll" @change="toggleSelectAll" size="sm"></b-form-checkbox>
                </th>
                <th class="srr sticky-col-2" @click="sortby('product_name')" style="min-width: 300px; width: 300px; max-width: 300px; background:lightgoldenrodyellow; z-index: 4;">PRODUCT NAME</th>
                <th class="srr sticky-col-3" @click="sortby('sku')" style="min-width: 250px; width: 250px; max-width: 250px; background:lightgoldenrodyellow; z-index: 3;">PRODUCT SKU</th>
                <th class="srr" v-if="!showSimplifiedColumns && Showimage" @click="sortby('image')" style="min-width: 120px; width: 120px; background:lightgoldenrodyellow;">IMAGE</th>
                <th class="srr" v-if="!showSimplifiedColumns && ShowBarcode" style="min-width: 100px; width: 100px; background:lightgoldenrodyellow;">BARCODE</th>
                <th class="srr" v-if="!showSimplifiedColumns && showMainColumns" @click="sortby('cubes')" style="min-width: 100px; width: 100px; background:lightgoldenrodyellow;">CUBES</th>
                <th class="srr" v-if="!showSimplifiedColumns && showMainColumns" @click="sortby('is_parent')" style="min-width: 100px; width: 100px; background:lightgoldenrodyellow;">GRP</th>
                <th class="srr" v-if="!showSimplifiedColumns && showMainColumns" @click="sortby('category_id')" style="min-width: 100px; width: 100px; background:lightgoldenrodyellow;">CATEGORY</th>
                <th class="srr" v-if="!showSimplifiedColumns && showMainColumns" @click="sortby('subcategory_id')" style="min-width: 100px; width: 100px; background:lightgoldenrodyellow;">SUB CATEGORY</th>
                <th class="srr" v-if="!showSimplifiedColumns && showMainColumns" @click="sortby('vendor_id')" style="min-width: 180px; width: 180px; background:lightgoldenrodyellow;">VENDOR</th>
                <th class="srr" v-if="!showSimplifiedColumns && showMainColumns" @click="sortby('quantity_per_box')" style="min-width: 100px; width: 100px; background:lightgoldenrodyellow;">QTY/BOX</th>
                <th class="srr" v-if="!showSimplifiedColumns && showMainColumns" style="min-width: 100px; width: 100px; background:lightgoldenrodyellow;">SPIFF</th>
                <th class="srr" v-if="!showSimplifiedColumns && showDetailColumns" @click="sortby('type_name')" style="min-width: 100px; width: 100px; background:lightcyan;">TYPE</th>
                <th class="srr" v-if="!showSimplifiedColumns && showDetailColumns" @click="sortby('subcategory_name')" style="min-width: 100px; width: 100px; background:lightcyan;">SUB CATEGORY</th>
                <th class="srr" v-if="!showSimplifiedColumns && showDetailColumns" @click="sortby('color_name')" style="min-width: 100px; width: 100px; background:lightcyan;">COLOR</th>
                <th class="srr" v-if="!showSimplifiedColumns && showDetailColumns" @click="sortby('size_name')" style="min-width: 100px; width: 100px; background:lightcyan;">SIZE</th>
                <th class="srr" v-if="!showSimplifiedColumns && showDetailColumns" @click="sortby('material_name')" style="min-width: 100px; width: 100px; background:lightcyan;">MATERIAL</th>
                <th class="srr" v-if="!showSimplifiedColumns && showDetailColumns" @click="sortby('functionality_name')" style="min-width: 100px; width: 100px; background:lightcyan;">FUNCTIONALITY</th>
                <th class="srr" @click="sortby('selling_price')" style="min-width: 100px; width: 100px; background:lightpink;">PRICE</th>
                <th class="srr" v-if="packet.userDetails.userrole === 'admin' && !showSimplifiedColumns && showPriceColumns" @click="sortby('fob_price')" style="min-width: 100px; width: 100px; background:lightpink;">FOB PRICE</th>
                <th class="srr" v-if="packet.userDetails.userrole === 'admin' && !showSimplifiedColumns && showPriceColumns" @click="sortby('landed_cost')" style="min-width: 100px; width: 100px; background:lightpink;">LANDED ($)</th>
                <th class="srr" v-if="packet.userDetails.userrole === 'admin' && !showSimplifiedColumns && showPriceColumns" @click="sortby('wholesale_price')" style="min-width: 100px; width: 100px; background:lightpink;">WHLSLE ($)</th>
                <th class="srr" v-if="!showSimplifiedColumns && showDiscountColumns" style="min-width: 150px; width: 150px; color:white; background:red;">SPECIAL DISCOUNT</th>
                <th class="srr" v-if="!showSimplifiedColumns && showDiscountColumns" style="min-width: 150px; width: 150px; color:white; background:red;">DISCOUNT ENDS</th>
                <th class="srr" v-if="packet.userDetails.userrole === 'admin' && !showSimplifiedColumns && showDiscountColumns" @click="sortby('profit_margin')" style="min-width: 120px; width: 120px; color:white; background:red;">PROFIT MARGIN</th>
                <th class="srr" @click="sortby('in_stock')" style="min-width: 100px; width: 100px; background:lightblue;">IN STOCK</th>
                <th class="srr" @click="sortby('reserved')" style="min-width: 100px; width: 100px; background:lightblue;">RESERVED</th>
                <th class="srr" @click="sortby('available')" style="min-width: 100px; width: 100px; background:lightblue;">AVAIL</th>
                <th class="srr" v-if="!showSimplifiedColumns" @click="sortby('layawayed')" style="min-width: 100px; width: 100px; background:lightblue;">LAYAWAYED</th>
                <th class="srr" v-if="!showSimplifiedColumns" @click="sortby('missing')" style="min-width: 100px; width: 100px; background:lightblue;">MISSING</th>
                <th class="srr" @click="sortby('sold_so_far')" style="min-width: 100px; width: 100px; background:lightgreen;">SOLD SO FAR</th>
                <th class="srr" @click="sortby('ordered')" style="min-width: 100px; width: 100px; background:lightgreen;">ORDERED</th>
                <th class="srr" @click="sortby('scheduled')" style="min-width: 100px; width: 100px; background:lightgreen;">SCHEDULED</th>
                <th v-if="packet.userDetails.userrole === 'admin'" class="srr sticky-last" style="min-width: 130px; width: 130px; max-width: 130px; background:lightgoldenrodyellow; z-index: 3;">ACTION</th>
            </tr>
        </thead>
        <tbody>
            <!-- Stats Summary Row -->
            <tr v-if="showCategoryStats && categoryStats.category_id" class="stats-summary-row">
                <td v-if="showSelectionMode" class="srrv sticky-col" style="min-width: 40px; width: 40px; max-width: 40px; background:#e8f4fd; z-index: 5;"></td>
                <td class="srrv sticky-col-2" style="min-width: 300px; width: 300px; max-width: 300px; background:#e8f4fd; z-index: 4; font-weight:bold; text-align:left;">
                    <span class="stats-summary-label">
                        <b-icon icon="bar-chart-fill" class="mr-1" variant="info"></b-icon>
                        CATEGORY TOTALS
                    </span>
                </td>
                <td class="srrv sticky-col-3" style="min-width: 250px; width: 250px; max-width: 250px; background:#e8f4fd; z-index: 3;">
                    <span class="stats-summary-value">{{ categoryStats.category_name }}</span>
                </td>
                <td v-if="!showSimplifiedColumns && Showimage" style="background:#e8f4fd; min-width: 120px; width: 120px;"></td>
                <td v-if="!showSimplifiedColumns && ShowBarcode" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="!showSimplifiedColumns && showMainColumns" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="!showSimplifiedColumns && showMainColumns" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="!showSimplifiedColumns && showMainColumns" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="!showSimplifiedColumns && showMainColumns" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="!showSimplifiedColumns && showMainColumns" style="background:#e8f4fd; min-width: 180px; width: 180px;"></td>
                <td v-if="!showSimplifiedColumns && showMainColumns" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="!showSimplifiedColumns && showMainColumns" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="!showSimplifiedColumns && showDetailColumns" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="!showSimplifiedColumns && showDetailColumns" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="!showSimplifiedColumns && showDetailColumns" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="!showSimplifiedColumns && showDetailColumns" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="!showSimplifiedColumns && showDetailColumns" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="!showSimplifiedColumns && showDetailColumns" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="packet.userDetails.userrole === 'admin' && !showSimplifiedColumns && showPriceColumns" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="packet.userDetails.userrole === 'admin' && !showSimplifiedColumns && showPriceColumns" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="packet.userDetails.userrole === 'admin' && !showSimplifiedColumns && showPriceColumns" style="background:#e8f4fd; min-width: 100px; width: 100px;"></td>
                <td v-if="!showSimplifiedColumns && showDiscountColumns" style="background:#e8f4fd; min-width: 150px; width: 150px;"></td>
                <td v-if="!showSimplifiedColumns && showDiscountColumns" style="background:#e8f4fd; min-width: 150px; width: 150px;"></td>
                <td v-if="packet.userDetails.userrole === 'admin' && !showSimplifiedColumns && showDiscountColumns" style="background:#e8f4fd; min-width: 120px; width: 120px;"></td>
                <td class="srrv stats-highlight" style="min-width: 100px; width: 100px; background:#d1ecf1; font-weight:bold; color:#0c5460;">{{ categoryStats.total_in_stock }}</td>
                <td class="srrv stats-highlight" style="min-width: 100px; width: 100px; background:#fff3cd; font-weight:bold; color:#856404;">{{ categoryStats.total_reserved }}</td>
                <td class="srrv stats-highlight" style="min-width: 100px; width: 100px; background:#d4edda; font-weight:bold; color:#155724;">{{ categoryStats.total_available }}</td>
                <td class="srrv stats-highlight" v-if="!showSimplifiedColumns" style="min-width: 100px; width: 100px; background:#e2e3e5; font-weight:bold; color:#383d41;">{{ categoryStats.total_layawayed }}</td>
                <td class="srrv stats-highlight" v-if="!showSimplifiedColumns" style="min-width: 100px; width: 100px; background:#f8d7da; font-weight:bold; color:#721c24;">{{ categoryStats.total_missing }}</td>
                <td class="srrv stats-highlight" style="min-width: 100px; width: 100px; background:#d4edda; font-weight:bold; color:#155724;">{{ categoryStats.total_sold_so_far }}</td>
                <td class="srrv stats-highlight" style="min-width: 100px; width: 100px; background:#d1ecf1; font-weight:bold; color:#0c5460;">{{ categoryStats.total_ordered }}</td>
                <td class="srrv stats-highlight" style="min-width: 100px; width: 100px; background:#fff3cd; font-weight:bold; color:#856404;">{{ categoryStats.total_scheduled }}</td>
                <td style="background:#e8f4fd; min-width: 140px; width: 140px;"></td>
                <td v-if="packet.userDetails.userrole === 'admin'" class="srrv sticky-last" style="min-width: 130px; width: 130px; max-width: 130px; background:#e8f4fd; z-index: 3;"></td>
            </tr>
            
            <!-- Regular product rows -->
            <tr v-for="(item, idx) in filtered_final" :key="item.sku + '-' + idx">
                <td v-if="showSelectionMode" class="srrv sticky-col" style="min-width: 40px; width: 40px; max-width: 40px; z-index: 5;">
                    <b-form-checkbox v-model="selectedProducts" :value="item.sku" @change="updateSelectAll" size="sm"></b-form-checkbox>
                </td>
                <td class="srrv sticky-col-2" style="min-width: 300px; width: 300px; max-width: 300px; z-index: 4;">
                    <b-input size="sm" v-model="item.product_name" v-if="item.edit == true" class="edit"></b-input>
                    <span v-else><b-alert variant="secondary" show style="padding:2px;">{{item.product_name}}</b-alert></span>
                </td>
                <td class="srrv sticky-col-3" style="min-width: 250px; width: 250px; max-width: 250px; z-index: 3;">
                    <b-alert variant="secondary" show style="padding:2px;">{{item.sku}} <br>
                        <span>
                            <badge v-if="item.discontinued" variant="danger" class="ml-1" style="font-size: 0.5rem; background-color: #dc3545; color: white; padding: 0.25rem 0.5rem; border-radius: 0.25rem;">
                                Discontinued
                            </badge>

                            <badge v-if="item.is_new_arrival" variant="success" class="ml-1" style="font-size: 0.55rem; background-color: #28a745; color: white; padding: 0.25rem 0.5rem; border-radius: 0.25rem;">
                                New Arrival
                            </badge>

                            <badge v-if="item.recently_received" variant="info" class="ml-1" style="font-size: 0.55rem; background-color: #17a2b8; color: white; padding: 0.25rem 0.5rem; border-radius: 0.25rem;">
                                <b-icon icon="box-seam" class="mr-1"></b-icon>
                                Recently Received
                            </badge>
                        </span>
                    </b-alert>
                </td>
                <!-- Rest of the columns remain the same -->
                <td v-if="!showSimplifiedColumns && Showimage" class="srrv" style="min-width: 120px; width: 120px; text-align:center; padding:5px;">
                    <div style="text-align:center; margin-bottom:5px; height:70px; display:flex; align-items:center; justify-content:center;">
                        <div v-if="getProductImage(item)" class="image-container">
                            <img :src="getProductImage(item)" :alt="item.product_name || item.sku" style="max-width:100%; max-height:80px; object-fit:contain; cursor:pointer;" @error="handleImageError($event, item)" @load="handleImageLoad($event, item)" @click="showFullImage(item)" class="product-image" />
                            <div class="image-overlay" @click="showFullImage(item)">🔍</div>
                        </div>
                        <div v-else class="no-image-placeholder">
                            <div style="color:#999; font-size:10px;">No Image</div>
                        </div>
                    </div>
                </td>
                <td v-if="!showSimplifiedColumns && ShowBarcode" class="srrv" style="min-width: 100px; width: 100px; text-align:center; padding:5px;">
                    <div v-if="item.barcode_image" style="text-align:center;">
                        <img :src="item.barcode_image" :alt="'Barcode for ' + item.sku" style="max-width:100%; max-height:50px; object-fit:contain; cursor:pointer;" @click="showFullBarcode(item)" @error="handleBarcodeImageError" class="barcode-image" />
                        <div class="barcode-hover" @click="showFullBarcode(item)">🔍</div>
                    </div>
                    <div v-else class="no-barcode-placeholder">
                        <b-button size="sm" variant="outline-primary" @click="generateSingleBarcode(item)" :disabled="generatingSingleBarcode">
                            <b-icon icon="qr-code" aria-hidden="true"></b-icon> Generate
                        </b-button>
                    </div>
                </td>
                <td class="srrv" v-if="!showSimplifiedColumns && showMainColumns" style="min-width: 100px; width: 100px;">
                    <b-input size="sm" v-model="item.cubes" type="number" v-if="item.edit == true && item.is_parent ==false" class="edit"></b-input>
                    <span v-else>{{item.cubes}}</span>
                </td>
                <td v-if="!showSimplifiedColumns && showMainColumns" class="srrv" style="min-width: 100px; width: 100px;">
                    <b-button size="sm" variant="success" :disabled="true" v-if="item.is_parent">YES</b-button>
                </td>
                <td v-if="!showSimplifiedColumns && showMainColumns" class="srrv" style="min-width: 100px; width: 100px;">
                    <span v-if="item.edit"><b-form-select :options="packet.categories" v-model="item.category_id"></b-form-select></span>
                    <span v-else>{{packet.categoriesdict[item.category_id]}}</span>
                </td>
                <td v-if="!showSimplifiedColumns && showMainColumns" class="srrv" style="min-width: 100px; width: 100px;">
                    <span v-if="item.edit"><b-form-select :options="subcategoryOptions" v-model="item.subcategory_id"></b-form-select></span>
                    <span v-else>{{ getSubcategoryLabel(item.subcategory_id) }}</span>
                </td>
                <td v-if="!showSimplifiedColumns && showMainColumns" class="srrv" style="min-width: 180px; width: 180px;">
                    <span v-if="item.edit"><b-form-select :options="packet.vendors" v-model="item.vendor_id"></b-form-select></span>
                    <span v-else>{{packet.vendorsdict[item.vendor_id]}}</span>
                </td>
                <td class="srrv" v-if="!showSimplifiedColumns && showMainColumns" style="min-width: 100px; width: 100px;">
                    <b-input size="sm" v-model="item.quantity_per_box" type="number" v-if="item.edit == true && item.is_parent ==false" class="edit"></b-input>
                    <span v-else>{{item.quantity_per_box || (item.properties && item.properties.quantity_per_box) || 1}}</span>
                </td>
                <td class="srrv" v-if="!showSimplifiedColumns && showMainColumns" style="min-width: 100px; width: 100px;">
                    <span v-if="item.edit == true && ['officemanager', 'admin'].includes(packet.userDetails.userrole)">
                        <b-input size="sm" v-model="item.spiff" type="number" class="edit"></b-input>
                    </span>
                    <span v-else>{{item.spiff}}</span>
                </td>
                <td v-if="!showSimplifiedColumns && showDetailColumns" class="srrv" style="min-width: 100px; width: 100px;">
                    <span v-if="item.edit"><b-form-select :options="typeOptions" v-model="item.type_id"></b-form-select></span>
                    <span v-else>{{ item.type_name || item.type_code || 'N/A' }}</span>
                </td>
                <td v-if="!showSimplifiedColumns && showDetailColumns" class="srrv" style="min-width: 100px; width: 100px;">
                    <span v-if="item.edit"><b-form-select :options="subcategoryOptions" v-model="item.subcategory_id"></b-form-select></span>
                    <span v-else>{{ item.subcategory_name || getSubcategoryLabel(item.subcategory_id) || 'N/A' }}</span>
                </td>
                <td v-if="!showSimplifiedColumns && showDetailColumns" class="srrv" style="min-width: 100px; width: 100px;">
                    <span v-if="item.edit"><b-form-select :options="colorOptions" v-model="item.color_id"></b-form-select></span>
                    <span v-else>{{ item.color_name || item.color_code || 'N/A' }}</span>
                </td>
                <td v-if="!showSimplifiedColumns && showDetailColumns" class="srrv" style="min-width: 100px; width: 100px;">
                    <span v-if="item.edit"><b-form-select :options="sizeOptions" v-model="item.size_id"></b-form-select></span>
                    <span v-else>{{ item.size_name || item.size_code || 'N/A' }}</span>
                </td>
                <td v-if="!showSimplifiedColumns && showDetailColumns" class="srrv" style="min-width: 100px; width: 100px;">
                    <span v-if="item.edit"><b-form-select :options="materialOptions" v-model="item.material_id"></b-form-select></span>
                    <span v-else>{{ item.material_name || item.material_code || 'N/A' }}</span>
                </td>
                <td v-if="!showSimplifiedColumns && showDetailColumns" class="srrv" style="min-width: 100px; width: 100px;">
                    <span v-if="item.edit"><b-form-select :options="functionalityOptions" v-model="item.functionality_id"></b-form-select></span>
                    <span v-else>{{ item.functionality_name || item.functionality_code || 'N/A' }}</span>
                </td>
                <td class="srrv" style="min-width: 100px; width: 100px;">
                    <b-input size="sm" v-model="item.selling_price" type="number" v-if="item.edit == true && item.is_parent == false" class="edit"></b-input>
                    <span v-else>
                        <span v-if="hasActiveDiscount(item)" class="discounted-price">
                            <strong style="color:#dc3545;">{{ calculateDiscountedPrice(item) }}</strong>
                            <small style="text-decoration:line-through; color:#6c757d; margin-left:5px;">{{ item.selling_price }}</small>
                        </span>
                        <span v-else>{{ item.selling_price }}</span>
                    </span>
                </td>
                <td v-if="packet.userDetails.userrole === 'admin' && !showSimplifiedColumns && showPriceColumns" class="srrv" style="min-width: 100px; width: 100px;">
                    <b-input size="sm" v-model="item.fob_price" type="number" v-if="item.edit == true && item.is_parent ==false" class="edit"></b-input>
                    <span v-else>{{item.fob_price}}</span>
                </td>
                <td v-if="packet.userDetails.userrole === 'admin' && !showSimplifiedColumns && showPriceColumns" class="srrv" style="min-width: 100px; width: 100px;">
                    <span v-if="item.edit == true && item.is_parent ==false && ['officemanager', 'admin'].includes(packet.userDetails.userrole)">
                        <b-input size="sm" v-model="item.landed_cost" type="number" class="edit"></b-input>
                    </span>
                    <span v-else>{{item.landed_cost}}</span>
                </td>
                <td v-if="packet.userDetails.userrole === 'admin' && !showSimplifiedColumns && showPriceColumns" class="srrv" style="min-width: 100px; width: 100px;">
                    <span v-if="item.edit == true && item.is_parent ==false && ['officemanager', 'admin'].includes(packet.userDetails.userrole)">
                        <b-input size="sm" v-model="item.wholesale_price" type="number" class="edit"></b-input>
                    </span>
                    <span v-else>{{item.wholesale_price}}</span>
                </td>
                <td class="srrv" v-if="!showSimplifiedColumns && showDiscountColumns" style="min-width: 150px; width: 150px;">
                    <span v-if="item.edit">
                        <b-input v-model="item.special_discount_price" type="number" min="0" step="0.01" class="edit" style="text-align:center;" placeholder="Discount Price"></b-input>
                    </span>
                    <span v-else>
                        <b-badge v-if="hasActiveDiscount(item)" variant="success">${{ item.special_discount_price }}</b-badge>
                        <span v-else class="text-muted">No discount</span>
                    </span>
                </td>
                <td class="srrv" v-if="!showSimplifiedColumns && showDiscountColumns" style="min-width: 150px; width: 150px;">
                    <span v-if="item.edit">
                        <date-picker v-model="item.special_discount_end_date" format="MM-DD-YYYY" value-type="format" style="padding:4px; font-size:12px; text-align:center; border:1px solid gray; width:125px;" :clearable="false" placeholder="Select end date" class="edit"></date-picker>
                    </span>
                    <span v-else>
                        <b-badge v-if="hasActiveDiscount(item)" variant="warning">{{ formatDate(item.special_discount_end_date) }}</b-badge>
                        <b-badge v-else-if="item.special_discount_end_date && isDiscountExpired(item)" variant="secondary">Expired</b-badge>
                        <span v-else class="text-muted">-</span>
                    </span>
                </td>
                <td v-if="packet.userDetails.userrole === 'admin' && !showSimplifiedColumns && showDiscountColumns" class="srrv" style="min-width: 120px; width: 120px;">
                    <span v-if="item.edit == true && item.is_parent ==false">
                        <b-input size="sm" v-model="item.selling_price" type="number" class="edit"></b-input>
                    </span>
                    <span v-else>{{ calculateProfitMargin(item.landed_cost, item.selling_price) }}%</span>
                 </td>
                <td class="srrv" style="min-width: 100px; width: 100px;">{{item.in_stock}}</td>
                <td class="srrv" style="min-width: 100px; width: 100px;">{{item.reserved}}</td>
                <td class="srrv" style="min-width: 100px; width: 100px;">{{item.available}}</td>
                <td class="srrv" v-if="!showSimplifiedColumns" style="min-width: 100px; width: 100px;">{{item.layawayed}}</td>
                <td class="srrv" v-if="!showSimplifiedColumns" style="min-width: 100px; width: 100px;">{{item.missing}}</td>
                <td class="srrv clickable-cell" @click="show_sold_details(item)" :title="`Sold: ${item.sold_so_far}\nClick for time breakdown`" style="min-width: 100px; width: 100px;">
                    {{item.sold_so_far}}<br><small style="font-size:9px; color:#666;">Click for details</small>
                 </td>
                <td class="srrv" style="min-width: 100px; width: 100px;">{{item.ordered}}</td>
                <td class="srrv clickable-cell" @click="show_scheduled_details(item)" :title="`Scheduled: ${item.scheduled}\nClick for time breakdown`" style="min-width: 100px; width: 100px;">
                    {{item.scheduled}}<br><small style="font-size:9px; color:#666;">Click for details</small>
                 </td>
                <td v-if="packet.userDetails.userrole === 'admin'" class="srrv sticky-last" style="min-width: 130px; width: 130px; max-width: 130px; z-index: 3;">
                    <b-card-text>
                        <span v-if="item.edit == true">
                            <b-button size="sm" variant="primary" v-if="['admin', 'officemanager'].includes(packet.userDetails.userrole)" @click="update_product(item)" v-b-tooltip.hover title="SAVE EDIT">
                                <b-icon icon="check-square" aria-hidden="true"></b-icon>
                            </b-button>
                            <b-button size="sm" variant="secondary" v-if="['admin', 'officemanager'].includes(packet.userDetails.userrole)" @click="item.edit = !item.edit" v-b-tooltip.hover title="CANCEL EDIT">
                                <b-icon icon="x" aria-hidden="true"></b-icon>
                            </b-button>
                        </span>
                        <span v-else>
                            <b-button size="sm" variant="outline-primary" v-if="['admin', 'officemanager'].includes(packet.userDetails.userrole)" @click="item.edit = !item.edit" v-b-tooltip.hover title="EDIT PRODUCT">
                                <b-icon icon="pencil-square" aria-hidden="true"></b-icon>
                            </b-button>
                        </span>
                        <b-button size="sm" variant="outline-info" v-if="packet.userDetails.userrole === 'admin'" @click="openImageUpload(item)" v-b-tooltip.hover title="UPLOAD/CHANGE IMAGE">
                            <b-icon icon="image" aria-hidden="true"></b-icon>
                        </b-button>
                        <b-button size="sm" :variant="isLowInventoryTriggered(item) ? 'danger' : (hasLowInventoryAlert(item) ? 'warning' : 'outline-warning')" v-if="packet.userDetails.userrole === 'admin'" @click="showLowInventoryAlert(item)" v-b-tooltip.hover :title="hasLowInventoryAlert(item) ? (isLowInventoryTriggered(item) ? '⚠️ LOW INVENTORY! Click to modify alert' : 'Low inventory alert configured. Click to modify') : 'Configure low inventory alert'">
                            <b-icon :icon="isLowInventoryTriggered(item) ? 'exclamation-triangle-fill' : (hasLowInventoryAlert(item) ? 'bell-fill' : 'bell')" aria-hidden="true"></b-icon>
                            <span v-if="isLowInventoryTriggered(item)" class="ml-1">!</span>
                        </b-button>
                        <b-button size="sm" variant="outline-danger" v-if="packet.userDetails.userrole == 'admin'" :disabled="item.sold_so_far > 0" @click="delete_product(item)" v-b-tooltip.hover title="DELETE PRODUCT">
                            <b-icon icon="trash-fill" aria-hidden="true"></b-icon>
                        </b-button>
                        <span v-if="item.status == 'active'">
                            <b-button size="sm" variant="outline-dark" @click="change_product_status(item, 'notactive')" v-if="packet.userDetails.userrole == 'admin'" v-b-tooltip.hover title="DISABLE PRODUCT">
                                <b-icon icon="pause-btn" aria-hidden="true"></b-icon>
                            </b-button>
                        </span>
                        <span v-else>
                            <b-button size="sm" variant="outline-success" @click="change_product_status(item, 'active')" v-if="packet.userDetails.userrole == 'admin'" v-b-tooltip.hover title="ENABLE PRODUCT">
                                <b-icon icon="play-btn" aria-hidden="true"></b-icon>
                            </b-button>
                        </span>
                        <b-button size="sm" variant="outline-warning" v-if="packet.userDetails.userrole == 'admin'" @click="show_item_stats(item)" v-b-tooltip.hover title="PRODUCT STATS">
                            <b-icon icon="bar-chart-line" aria-hidden="true"></b-icon>
                        </b-button>
                    </b-card-text>
                 </td>
             </tr>
        </tbody>
    </table>

    <!-- Loading more indicator -->
    <div v-if="isLoadingMore" class="text-center p-3">
        <b-spinner small></b-spinner>
        <span style="margin-left:10px;">Loading more products...</span>
    </div>

    <div v-if="!hasMore && displayedItems.length > 0" class="text-center p-2" style="background:#f8f9fa; border-top:1px solid #dee2e6;">
        <small class="text-muted">All {{ totalItemsCount }} products loaded</small>
    </div>
</div>

            <div v-else-if="!items_being_loaded">
                <b-alert variant="warning" show>No products to show</b-alert>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import global_vars from "@/utilities/global_vars";
import Backer from "@/components/globals/Backer";
import Requestor from "@/utilities/Requestor";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import "vue-good-table/dist/vue-good-table.css";
import {VueGoodTable} from "vue-good-table";
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import VirtualList from "vue-virtual-scroll-list";
import Product from "@/components/Products/Product";
import ProductStatsModal from "@/components/Products/ProductStatsModal";

export default {
    name: "ProductsListProducts",
    components: {
        Backer,
        Loading,
        VueGoodTable,
        VirtualList,
        Product,
        ProductStatsModal,
        DatePicker
    },
    data: function() {
        return {
            packet: {},
            bulkImages: [],
            bulkImagePreview: [],
            uploadingBulkImages: false,
            bulkImageProgress: { total: 0, processed: 0, successful: 0, failed: 0, current: 0 },
            showMoreFilters: false,  
            showMainColumns: false,
            showPriceColumns: false,
            showDiscountColumns: false,
            generatingBarcodes: false,
            bulkStatusModalVisible: false,
            bulkStatusAction: 'active', 
            updateChildren: false,
            ShowBarcode: true,
            generatingSingleBarcode: false,
            currentBarcodeImage: null,
            currentProductSku: null,
            showLowInventoryModal: false,
            currentAlertProduct: null,
            alertConfig: { alert_type: 'threshold', threshold_quantity: 10, notification_methods: ['email', 'dashboard'], alert_emails: '', notes: '' },
            currentInventoryLevel: null,
            alertTypeOptions: [{ text: 'Threshold Alert', value: 'threshold' }, { text: 'Percentage Alert', value: 'percentage' }, { text: 'Reorder Point', value: 'reorder_point' }],
            barcodeProgress: { total: 0, processed: 0, successful: 0, failed: 0, percentage: 0, message: '', errors: [] },
            Showimage: false,
            showDetailColumns: false,
            selectedProducts: [],
            showSelectionMode: false,
            selectAll: false,
            typeFilter: null,
            colorFilter: null,
            sizeFilter: null,
            materialFilter: null,
            functionalityFilter: null,
            typeOptions: [],
            colorOptions: [],
            sizeOptions: [],
            materialOptions: [],
            functionalityOptions: [],
            itemStatusFilter: null,
            itemStatusOptions: [{ value: null, text: 'ALL ITEM STATUSES' }, { value: 'in_stock', text: 'IN STOCK' }, { value: 'out_of_stock', text: 'OUT OF STOCK' }, { value: 'new_product', text: 'NEW PRODUCT' }, { value: 'ordered_product', text: 'ORDERED PRODUCT' }],
            allItems: [],
            displayedItems: [],
            currentOffset: 0,
            subcategory_id: null,
            uploadProduct: null,
            selectedFile: null,
            imagePreview: null,
            uploadingImage: false,
            subcategories: [],
            pageSize: 100,
            isLoadingMore: false,
            hasMore: true,
            totalItemsCount: 0,
            currentFullSizeImage: null,
            currentProductName: null,
            itemTypeFilter: null,
            itemTypeOptions: [{ value: null, text: 'ALL ITEM TYPES' }, { value: 'single', text: 'SINGLE ITEMS ONLY' }, { value: 'group', text: 'GROUP ITEMS ONLY' }],
            downloadingExcel: false,
            vendor_id: null,
            category_id: null,
            location_id: null,
            showDisabled: false,
            items_being_loaded: false,
            global_vars: global_vars,
            edit_product_idx: null,
            currentPage: 1,
            totalPages: 0,
            sort_asc: true,
            product_query: null,
            limit: 100,
            columns: [],
            searchValue: "",
            itemComponent: Product,
            pageIndex: 1,
            csvFile: null,
            csvPreview: [],
            uploadingCsv: false,
            csvFields: [{ key: 'sku', label: 'SKU' }, { key: 'description', label: 'DESCRIPTION' }, { key: 'category', label: 'CATEGORY' }, { key: 'sub_category', label: 'SUB CATEGORY' }, { key: 'vendor', label: 'VENDOR' }, { key: 'quantity_per_box', label: 'QTY PER BOX' }, { key: 'fob_price', label: 'FOB PRICE' }, { key: 'landed_cost', label: 'LANDED CST' }, { key: 'wholesale_price', label: 'WHLSL PRICE' }, { key: 'selling_price', label: 'SELL PRICE' }, { key: 'cubes', label: 'CUBES' }, { key: 'spiff', label: 'SPIFF' }],
            currentProduct: null,
            detailFields: [{ key: 'period', label: 'Time Period', sortable: true }, { key: 'value', label: 'Quantity', sortable: true }],
            statusFilter: 'active',
            statusOptions: [{ value: 'all', text: 'ALL STATUSES' }, { value: 'active', text: 'ACTIVE ONLY' }, { value: 'notactive', text: 'DISABLED ONLY' }],
            bestSellingFilter: null,
            bestSellingOptions: [{ value: null, text: 'ALL PRODUCTS' }, { value: '7_days', text: 'BEST SELLERS FOR THE WEEK' }, { value: '30_days', text: 'BEST SELLERS FOR THE MONTH' }, { value: '60_days', text: 'BEST SELLERS FOR LAST 2 MONTHS' }, { value: '90_days', text: 'BEST SELLERS FOR LAST 3 MONTHS' }, { value: 'all_time', text: 'BEST SELLERS - ALL TIME' }, { value: 'profit_margin', text: 'HIGHEST PROFIT MARGIN %' }, { value: 'profit_7_days', text: 'HIGHEST PROFIT FOR THE WEEK' }, { value: 'profit_30_days', text: 'HIGHEST PROFIT FOR THE MONTH' }, { value: 'profit_60_days', text: 'HIGHEST PROFIT FOR LAST 2 MONTHS' }, { value: 'profit_90_days', text: 'HIGHEST PROFIT FOR LAST 3 MONTHS' }, { value: 'profit_all_time', text: 'HIGHEST PROFIT (ALL TIME)' }],
            slowestSellingFilter: null,
            slowestSellingOptions: [{ value: null, text: 'ALL PRODUCTS' }, { value: 'slowest_7_days', text: 'SLOWEST SELLERS FOR THE WEEK' }, { value: 'slowest_30_days', text: 'SLOWEST SELLERS FOR THE MONTH' }, { value: 'slowest_60_days', text: 'SLOWEST SELLERS FOR LAST 2 MONTHS' }, { value: 'slowest_90_days', text: 'SLOWEST SELLERS FOR LAST 3 MONTHS' }, { value: 'slowest_all_time', text: 'SLOWEST SELLERS - ALL TIME' }],
            bestSellingSortDesc: true,
            filtered_items: [],
            stats_item: null,
            user_location_id: null,
            user_location_name: "",
            locationsDict: {},
            searchTimeout: null,
            lastScrollTop: 0,
            filtersVisible: false,
            isMobile: false,
            // Category Stats Data
            showCategoryStats: false,
            isLoadingCategoryStats: false,
            categoryStats: {
                category_name: '',
                category_code: '',
                category_id: null,
                total_in_stock: 0,
                total_reserved: 0,
                total_available: 0,
                total_layawayed: 0,
                total_missing: 0,
                total_sold_so_far: 0,
                total_ordered: 0,
                total_scheduled: 0,
                total_products: 0,
                products_in_stock: 0,
                products_out_of_stock: 0,
                parent_products: 0,
                child_products: 0
            },
            // PRINT BARCODE DATA
            printProducts: [],
            isPrinting: false,
            printSettings: { labelSize: '1.5x2', copies: 1, printMethod: 'browser', layout: 'single', showPrice: true, showProductName: true, showSku: true },
            labelSizeOptions: [{ value: '1.5x2', text: '1.5" x 2"' }, { value: '2x2', text: '2" x 2"' }, { value: '2x3', text: '2" x 3"' }, { value: '4x6', text: '4" x 6"' }],
            printMethodOptions: [{ value: 'browser', text: 'Browser Print' }, { value: 'newWindow', text: 'New Window' }, { value: 'direct', text: 'Direct Print' }],
            layoutOptions: [{ value: 'single', text: '1 per page' }, { value: 'double', text: '2 per page' }, { value: 'grid', text: 'Grid 2x2' }],
            labelDimensions: { '1.5x2': { w: 1.5, h: 2 }, '2x2': { w: 2, h: 2 }, '2x3': { w: 2, h: 3 }, '4x6': { w: 4, h: 6 } },
            // Discontinued data
            discontinuedFilter: null,
            discontinuedOptions: [
                { value: null, text: 'ALL' },
                { value: 'active', text: 'ACTIVE PRODUCTS' },
                { value: 'discontinued', text: 'DISCONTINUED' }
            ],
            discontinuedModalVisible: false,
            bulkDiscontinuedAction: false,
            discontinuedReason: '',
            updateChildrenDiscontinued: false,
            // New Arrival data
            newArrivalFilter: null,
            newArrivalOptions: [
                { value: null, text: 'ALL' },
                { value: 'new', text: 'NEW ARRIVALS' },
                { value: 'expired', text: 'EXPIRED NEW ARRIVALS' }
            ],
            newArrivalModalVisible: false,
            bulkNewArrivalAction: false,
            newArrivalDays: 30,
            updateChildrenNewArrival: false,
            // SPIFF Filter
            spiffFilter: null,
            spiffFilterOptions: [
                { value: null, text: 'ALL ITEMS' },
                { value: 'with_spiff', text: 'WITH SPIFF (>0)' },
                { value: 'without_spiff', text: 'WITHOUT SPIFF (0)' },
                { value: 'high_spiff', text: 'HIGH SPIFF (>10)' }
            ],
            // Received Status Filter
            receivedStatusFilter: null,
            receivedStatusOptions: [
                { value: null, text: 'ALL' },
                { value: 'recently_received', text: 'RECENTLY RECEIVED' },
                { value: 'not_recently_received', text: 'NOT RECENTLY RECEIVED' }
            ],
        }
    },
    computed: {
        activeFiltersCount() {
            let count = 0;
            if (this.vendor_id) count++;
            if (this.category_id) count++;
            if (this.subcategory_id) count++;
            if (this.product_query) count++;
            if (this.location_id && this.packet.userDetails.userrole === 'admin') count++;
            if (this.itemTypeFilter) count++;
            if (this.itemStatusFilter) count++;
            if (this.statusFilter !== 'all') count++;
            if (this.discontinuedFilter) count++;
            if (this.newArrivalFilter) count++;
            if (this.receivedStatusFilter) count++;
            if (this.typeFilter) count++;
            if (this.colorFilter) count++;
            if (this.sizeFilter) count++;
            if (this.materialFilter) count++;
            if (this.functionalityFilter) count++;
            if (this.spiffFilter) count++;
            if (this.bestSellingFilter) count++;
            if (this.slowestSellingFilter) count++;
            return count;
        },
        getActiveFiltersList() {
            const filters = [];
            if (this.vendor_id) filters.push({ key: 'vendor', label: 'Vendor', value: this.packet.vendorsdict[this.vendor_id] });
            if (this.category_id) filters.push({ key: 'category', label: 'Category', value: this.packet.categoriesdict[this.category_id] });
            if (this.subcategory_id) filters.push({ key: 'subcategory', label: 'Sub Category', value: this.getSubcategoryLabel(this.subcategory_id) });
            if (this.product_query) filters.push({ key: 'search', label: 'Search', value: this.product_query });
            if (this.location_id && this.packet.userDetails.userrole === 'admin') filters.push({ key: 'location', label: 'Location', value: this.locationsDict[this.location_id] });
            if (this.itemTypeFilter) filters.push({ key: 'itemType', label: 'Item Type', value: this.itemTypeFilter === 'single' ? 'Single' : 'Group' });
            if (this.itemStatusFilter) filters.push({ key: 'itemStatus', label: 'Item Status', value: this.itemStatusOptions.find(opt => opt.value === this.itemStatusFilter).text });
            if (this.statusFilter !== 'all') filters.push({ key: 'status', label: 'Status', value: this.statusFilter === 'active' ? 'Active' : 'Disabled' });
            if (this.discontinuedFilter) filters.push({ key: 'discontinued', label: 'Discontinued', value: this.discontinuedFilter === 'discontinued' ? 'Discontinued' : 'Active' });
            if (this.newArrivalFilter) filters.push({ key: 'newArrival', label: 'New Arrival', value: this.newArrivalFilter === 'new' ? 'New Arrivals' : 'Expired' });
            if (this.receivedStatusFilter) {
                const receivedLabels = {
                    'recently_received': 'Recently Received',
                    'not_recently_received': 'Not Recently Received'
                };
                filters.push({ key: 'receivedStatus', label: 'Received Status', value: receivedLabels[this.receivedStatusFilter] || this.receivedStatusFilter });
            }
            if (this.typeFilter) filters.push({ key: 'type', label: 'Type', value: this.typeOptions.find(opt => opt.value === this.typeFilter).text });
            if (this.colorFilter) filters.push({ key: 'color', label: 'Color', value: this.colorOptions.find(opt => opt.value === this.colorFilter).text });
            if (this.sizeFilter) filters.push({ key: 'size', label: 'Size', value: this.sizeOptions.find(opt => opt.value === this.sizeFilter).text });
            if (this.materialFilter) filters.push({ key: 'material', label: 'Material', value: this.materialOptions.find(opt => opt.value === this.materialFilter).text });
            if (this.functionalityFilter) filters.push({ key: 'functionality', label: 'Functionality', value: this.functionalityOptions.find(opt => opt.value === this.functionalityFilter).text });
            if (this.spiffFilter) {
                const spiffLabels = {
                    'with_spiff': 'With SPIFF (>0)',
                    'without_spiff': 'Without SPIFF (0)',
                    'high_spiff': 'High SPIFF (>10)'
                };
                filters.push({ key: 'spiff', label: 'SPIFF', value: spiffLabels[this.spiffFilter] || this.spiffFilter });
            }
            if (this.bestSellingFilter) filters.push({ key: 'bestSelling', label: 'Best Selling', value: this.bestSellingOptions.find(opt => opt.value === this.bestSellingFilter).text });
            if (this.slowestSellingFilter) filters.push({ key: 'slowestSelling', label: 'Slowest Selling', value: this.slowestSellingOptions.find(opt => opt.value === this.slowestSellingFilter).text });
            return filters;
        },
        isAlertConfigValid() { 
            return this.currentAlertProduct && this.alertConfig.threshold_quantity > 0 && this.alertConfig.alert_type; 
        },
        showSimplifiedColumns() { 
            return this.bestSellingFilter || this.slowestSellingFilter; 
        },
        visibleColumns() {
            if (this.showSimplifiedColumns) {
                return ['product_name', 'sku', 'image', 'is_parent', 'category_id', 'subcategory_id', 'vendor_id', 'selling_price', 'in_stock', 'available', 'sold_so_far', 'ordered', 'scheduled', 'received'];
            }
            return 'all';
        },
        subcategoryOptions() {
            if (!this.category_id) return [{ value: null, text: 'Select Category First' }];
            const filteredSubcategories = this.subcategories.filter(subcat => subcat.category_id == this.category_id);
            return [{ value: null, text: 'ALL' }, ...filteredSubcategories.map(subcat => ({ value: subcat.id, text: subcat.subcategory }))];
        },
        filtered_final() {
            if (!this.hasActiveFilters && this.displayedItems.length === 0) return [];
            let items = [...this.displayedItems];
            
            if (this.statusFilter !== 'all') {
                items = items.filter(item => {
                    let itemStatus = item.status;
                    let filterStatus = this.statusFilter;
                    if (filterStatus === 'notactive') return itemStatus === 'notactive' || itemStatus === 'not-active';
                    return itemStatus === filterStatus;
                });
            }
            
            if (this.discontinuedFilter) {
                items = items.filter(item => {
                    if (this.discontinuedFilter === 'discontinued') {
                        return item.discontinued === true;
                    } else if (this.discontinuedFilter === 'active') {
                        return item.discontinued !== true;
                    }
                    return true;
                });
            }
            
            if (this.newArrivalFilter) {
                items = items.filter(item => {
                    if (this.newArrivalFilter === 'new') {
                        return item.is_new_arrival === true;
                    } else if (this.newArrivalFilter === 'expired') {
                        return item.is_new_arrival !== true;
                    }
                    return true;
                });
            }

            if (this.receivedStatusFilter) {
                items = items.filter(item => {
                    if (this.receivedStatusFilter === 'recently_received') {
                        return item.recently_received === true;
                    } else if (this.receivedStatusFilter === 'not_recently_received') {
                        return item.recently_received !== true;
                    }
                    return true;
                });
            }
            
            if (this.itemStatusFilter) {
                items = items.filter(item => {
                    const isOld = this.isOldProduct(item);
                    switch (this.itemStatusFilter) {
                        case 'in_stock': return item.in_stock > 0 && isOld;
                        case 'out_of_stock': return item.in_stock === 0 && isOld;
                        case 'new_product': return item.in_stock > 0 && !isOld;
                        case 'ordered_product': return item.ordered > 0 && item.in_stock === 0;
                        default: return true;
                    }
                });
            }
            
            if (this.itemTypeFilter === 'single') items = items.filter(item => !item.is_parent);
            else if (this.itemTypeFilter === 'group') items = items.filter(item => item.is_parent);
            
            // SPIFF Filter
            if (this.spiffFilter) {
                items = items.filter(item => {
                    const spiff = parseFloat(item.spiff) || 0;
                    switch (this.spiffFilter) {
                        case 'with_spiff': return spiff > 0;
                        case 'without_spiff': return spiff === 0;
                        case 'high_spiff': return spiff > 10;
                        default: return true;
                    }
                });
            }
            
            if (this.bestSellingFilter) items = this.applyBestSellingSort(items);
            if (this.slowestSellingFilter) items = this.applySlowestSellingSort(items);
            
            if (!this.product_query) return items;
            else {
                let product_query = this.product_query.toUpperCase();
                return items.filter(d => (d.product_name.includes(product_query)) || (d.sku.includes(product_query)));
            }
        },
        hasActiveFilters() {
            return this.category_id !== null || this.vendor_id !== null || (this.packet.userDetails.userrole === 'admin' && this.location_id !== null) || this.subcategory_id !== null || this.itemTypeFilter !== null || this.itemStatusFilter !== null || this.statusFilter !== 'all' || this.discontinuedFilter !== null || this.newArrivalFilter !== null || this.receivedStatusFilter !== null || this.bestSellingFilter !== null || this.slowestSellingFilter !== null || this.spiffFilter !== null || this.hasActiveDetailFilters || (this.product_query && this.product_query.trim().length > 0);
        },
        hasActiveDetailFilters() {
            return this.typeFilter !== null || this.colorFilter !== null || this.sizeFilter !== null || this.materialFilter !== null || this.functionalityFilter !== null;
        },
        etaDetails() {
            if (!this.currentProduct) return [];
            return [{ period: 'Total ETA', value: this.currentProduct.eta_all || 0 }, { period: 'LAST 7 Days', value: this.currentProduct.eta_7_days || 0 }, { period: 'LAST 30 Days', value: this.currentProduct.eta_30_days || 0 }, { period: 'LAST 60 Days', value: this.currentProduct.eta_60_days || 0 }, { period: 'LAST 90 Days', value: this.currentProduct.eta_90_days || 0 }].filter(detail => detail.value > 0);
        },
        soldDetails() {
            if (!this.currentProduct) return [];
            return [{ period: 'Total Sold', value: this.currentProduct.sold_so_far || 0 }, { period: 'LAST 7 Days', value: this.currentProduct.sold_7d || 0 }, { period: 'LAST 30 Days', value: this.currentProduct.sold_30d || 0 }, { period: 'LAST 60 Days', value: this.currentProduct.sold_60d || 0 }, { period: 'LAST 90 Days', value: this.currentProduct.sold_90d || 0 }].filter(detail => detail.value > 0);
        },
        receivedDetails() {
            if (!this.currentProduct) return [];
            return [{ period: 'Total Received', value: this.currentProduct.received || 0 }, { period: 'LAST 7 Days', value: this.currentProduct.received_7d || 0 }, { period: 'LAST 30 Days', value: this.currentProduct.received_30d || 0 }, { period: 'LAST 60 Days', value: this.currentProduct.received_60d || 0 }, { period: 'LAST 90 Days', value: this.currentProduct.received_90d || 0 }].filter(detail => detail.value > 0);
        },
        scheduledDetails() {
            if (!this.currentProduct) return [];
            return [{ period: 'Total Scheduled', value: this.currentProduct.scheduled || 0 }, { period: 'LAST 7 Days', value: this.currentProduct.scheduled_7d || 0 }, { period: 'LAST 30 Days', value: this.currentProduct.scheduled_30d || 0 }, { period: 'LAST 60 Days', value: this.currentProduct.scheduled_60d || 0 }, { period: 'LAST 90 Days', value: this.currentProduct.scheduled_90d || 0 }].filter(detail => detail.value > 0);
        },
        printAllPages() {
            if (!this.printSettings) return [];
            let all = [];
            for (let i = 0; i < this.printSettings.copies; i++) {
                all.push(...this.printProducts);
            }
            return all.slice(0, 24 * this.printSettings.copies);
        },
        showStatsButton() {
            return this.category_id !== null && this.category_id !== '';
        }
    },
    watch: {
        product_query: function(newVal) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                if (newVal && newVal.length >= 2) {
                    this.fetch_product_name_sku();
                } else if (!newVal) {
                    this.currentOffset = 0;
                    this.hasMore = false;
                    this.allItems = [];
                    this.displayedItems = [];
                    this.updateDisplayedItems();
                }
            }, 5000);
        },
        category_id: function(newCategoryId) {
            // Hide stats when category changes
            this.showCategoryStats = false;
            if (newCategoryId) {
                this.fetchAllHierarchyOptions(newCategoryId);
                this.resetDetailFilters();
            } else {
                this.fetchAllHierarchyOptions();
            }
        }
    },
    methods: {
        // ============================================
        // NEW: Category Stock Stats Methods
        // ============================================
        async fetchCategoryStockStats() {
            if (!this.category_id) {
                this.$bvToast.toast("Please select a category first", { 
                    title: "Warning", 
                    variant: "warning", 
                    autoHideDelay: 2000 
                });
                return;
            }
            
            this.isLoadingCategoryStats = true;
            this.showCategoryStats = false;
            
            try {
                let path = "/api/product/get_category_stock_stats/";
                let data = {
                    category_id: this.category_id,
                    user_role: this.packet.userDetails.userrole,
                    user_location_id: this.user_location_id
                };
                
                if (this.packet.userDetails.userrole === 'admin') {
                    data.location_id = this.location_id;
                }
                
                const response = await new Promise((resolve, reject) => {
                    Requestor.getSyncP(path, data, this, resolve, reject);
                });
                
                if (response.data.error) {
                    this.$bvToast.toast(response.data.error, { 
                        title: "Error", 
                        variant: "danger", 
                        autoHideDelay: 3000 
                    });
                } else {
                    this.categoryStats = {
                        category_name: response.data.result.category_name || '',
                        category_code: response.data.result.category_code || '',
                        category_id: response.data.result.category_id || this.category_id,
                        total_in_stock: response.data.result.total_in_stock || 0,
                        total_reserved: response.data.result.total_reserved || 0,
                        total_available: response.data.result.total_available || 0,
                        total_layawayed: response.data.result.total_layawayed || 0,
                        total_missing: response.data.result.total_missing || 0,
                        total_sold_so_far: response.data.result.total_sold_so_far || 0,
                        total_ordered: response.data.result.total_ordered || 0,
                        total_scheduled: response.data.result.total_scheduled || 0,
                        total_products: response.data.result.total_products || 0,
                        products_in_stock: response.data.result.products_in_stock || 0,
                        products_out_of_stock: response.data.result.products_out_of_stock || 0,
                        parent_products: response.data.result.parent_products || 0,
                        child_products: response.data.result.child_products || 0
                    };
                    this.showCategoryStats = true;
                    
                    this.$bvToast.toast(`Stats loaded for ${this.categoryStats.category_name}`, { 
                        title: "Success", 
                        variant: "success", 
                        autoHideDelay: 2000 
                    });
                }
            } catch (error) {
                console.error('Error fetching category stats:', error);
                this.$bvToast.toast("Error fetching category statistics", { 
                    title: "Error", 
                    variant: "danger", 
                    autoHideDelay: 3000 
                });
            } finally {
                this.isLoadingCategoryStats = false;
            }
        },

        // PRINT BARCODE METHODS
        resetAllFilters() {
            this.showMoreFilters = false;
            this.vendor_id = null;
            this.category_id = null;
            this.subcategory_id = null;
            this.product_query = null;
            this.itemTypeFilter = null;
            this.itemStatusFilter = null;
            this.statusFilter = 'all';
            this.discontinuedFilter = null;
            this.newArrivalFilter = null;
            this.receivedStatusFilter = null;
            this.typeFilter = null;
            this.colorFilter = null;
            this.sizeFilter = null;
            this.materialFilter = null;
            this.functionalityFilter = null;
            this.bestSellingFilter = null;
            this.slowestSellingFilter = null;
            this.spiffFilter = null;
            this.showCategoryStats = false;
            if (this.packet.userDetails.userrole === 'admin') {
                this.location_id = null;
            } else {
                this.location_id = this.user_location_id;
            }
            this.fetch_init(true, true);
        },
        removeFilter(filterKey) {
            switch(filterKey) {
                case 'vendor': this.vendor_id = null; break;
                case 'category': this.category_id = null; break;
                case 'subcategory': this.subcategory_id = null; break;
                case 'search': this.product_query = null; break;
                case 'location': this.location_id = this.user_location_id; break;
                case 'itemType': this.itemTypeFilter = null; break;
                case 'itemStatus': this.itemStatusFilter = null; break;
                case 'status': this.statusFilter = 'all'; break;
                case 'discontinued': this.discontinuedFilter = null; break;
                case 'newArrival': this.newArrivalFilter = null; break;
                case 'receivedStatus': this.receivedStatusFilter = null; break;
                case 'type': this.typeFilter = null; break;
                case 'color': this.colorFilter = null; break;
                case 'size': this.sizeFilter = null; break;
                case 'material': this.materialFilter = null; break;
                case 'functionality': this.functionalityFilter = null; break;
                case 'spiff': this.spiffFilter = null; break;
                case 'bestSelling': this.bestSellingFilter = null; break;
                case 'slowestSelling': this.slowestSellingFilter = null; break;
            }
            this.showCategoryStats = false;
            this.fetch_init(true, true);
        },
        clearAllFilters() {
            this.resetAllFilters();
        },
        openPrintModal() {
            if (this.selectedProducts.length === 0) {
                this.$bvToast.toast("Please select products first", { title: "No Selection", variant: "warning", autoHideDelay: 2000 });
                return;
            }
            if (this.selectedProducts.length > 24) {
                this.$bvToast.toast(`Maximum 24 products allowed. You selected ${this.selectedProducts.length}`, { title: "Limit Exceeded", variant: "warning", autoHideDelay: 3000 });
                return;
            }
            this.generateBarcodesForPrint();
            this.$bvModal.show('modal-print-barcodes');
        },
        async generateBarcodesForPrint() {
            this.printProducts = [];
            for (const sku of this.selectedProducts) {
                const existingProduct = this.allItems.find(p => p.sku === sku);
                if (existingProduct) {
                    const barcodeDataUrl = await this.generateBarcodeDataUrl(sku);
                    this.printProducts.push({
                        sku: existingProduct.sku,
                        product_name: existingProduct.product_name,
                        selling_price: existingProduct.selling_price,
                        barcodeDataUrl: barcodeDataUrl
                    });
                }
            }
        },
        generateBarcodeDataUrl(sku) {
            return new Promise((resolve) => {
                const existingProduct = this.allItems.find(p => p.sku === sku);
                if (existingProduct && existingProduct.barcode_image) {
                    resolve(existingProduct.barcode_image);
                    return;
                }
                const canvas = document.createElement('canvas');
                canvas.width = 300;
                canvas.height = 100;
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.fillStyle = '#000000';
                let x = 20;
                for (let i = 0; i < sku.length; i++) {
                    const code = sku.charCodeAt(i);
                    const width = (code % 8) + 2;
                    ctx.fillRect(x, 10, width, 70);
                    x += width + 2;
                }
                ctx.font = 'bold 14px monospace';
                ctx.fillStyle = '#000000';
                const textWidth = ctx.measureText(sku).width;
                ctx.fillText(sku, canvas.width / 2 - textWidth / 2, 95);
                resolve(canvas.toDataURL('image/png'));
            });
        },
        removePrintItem(index) {
            this.printProducts.splice(index, 1);
            if (this.printProducts.length < this.selectedProducts.length) {
                const removedSku = this.selectedProducts[index];
                this.selectedProducts = this.selectedProducts.filter(sku => sku !== removedSku);
                this.updateSelectAll();
            }
        },
        clearPrintAll() {
            this.printProducts = [];
            this.selectedProducts = [];
            this.selectAll = false;
            this.$bvModal.hide('modal-print-barcodes');
        },
        resetPrintModal() {
            this.printProducts = [];
            this.isPrinting = false;
            this.printSettings = { labelSize: '1.5x2', copies: 1, printMethod: 'browser', layout: 'single', showPrice: true, showProductName: true, showSku: true };
        },
        async executePrint() {
            if (this.isPrinting) return;
            this.isPrinting = true;
            try {
                if (this.printSettings.printMethod === 'browser') await this.printViaBrowser();
                else if (this.printSettings.printMethod === 'newWindow') await this.printViaNewWindow();
                else await this.printViaDirect();
                this.$bvToast.toast(`Printed ${this.printAllPages.length} label(s)`, { title: "Print Success", variant: "success", autoHideDelay: 3000 });
                this.$bvModal.hide('modal-print-barcodes');
            } catch (error) {
                console.error('Print error:', error);
                this.$bvToast.toast(error.message || "Print failed", { title: "Print Error", variant: "danger", autoHideDelay: 5000 });
            } finally {
                this.isPrinting = false;
            }
        },
        async printViaBrowser() {
            const dims = this.labelDimensions[this.printSettings.labelSize] || this.labelDimensions['1.5x2'];
            const allLabels = this.printAllPages;
            const printWindow = window.open('', '_blank');
            const labelHTML = allLabels.map((p, idx) => {
                return `<div class="print-label" style="width:${dims.w}in;height:${dims.h}in;page-break-after:always;padding:0.1in;text-align:center;box-sizing:border-box;border:1px solid #ccc;display:flex;flex-direction:column;justify-content:center;align-items:center;font-family:Arial,sans-serif;">
                    <img src="${p.barcodeDataUrl}" style="max-width:${dims.w - 0.3}in;max-height:${dims.h - 0.8}in;width:auto;height:auto;" />
                    ${this.printSettings.showSku ? `<div style="font-weight:bold;font-size:${dims.w >= 2 ? '12pt' : '10pt'};margin-top:0.05in;">${p.sku}</div>` : ''}
                    ${this.printSettings.showProductName ? `<div style="font-size:${dims.w >= 2 ? '10pt' : '8pt'};margin-top:0.03in;">${this.truncateText(p.product_name, dims.w >= 2 ? 40 : 25)}</div>` : ''}
                    ${this.printSettings.showPrice ? `<div style="font-size:${dims.w >= 2 ? '11pt' : '9pt'};color:#2e7d32;margin-top:0.03in;">$${this.formatPriceNum(p.selling_price)}</div>` : ''}
                </div>`;
            }).join('');
            printWindow.document.write(`<!DOCTYPE html><html><head><title>Print Barcodes</title><style>@page{size:${dims.w}in ${dims.h}in;margin:0.05in;}*{margin:0;padding:0;box-sizing:border-box;}body{margin:0;padding:0;}.print-label{page-break-after:always;page-break-inside:avoid;}@media print{body{margin:0;padding:0;}}</style></head><body>${labelHTML}<script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();};<\/script></body></html>`);
            printWindow.document.close();
        },
        async printViaNewWindow() {
            const dims = this.labelDimensions[this.printSettings.labelSize] || this.labelDimensions['1.5x2'];
            const allLabels = this.printAllPages;
            let printContent = `<html><head><title>Print Barcodes</title><style>@page{size:${dims.w}in ${dims.h}in;margin:0.05in;}body{margin:0;padding:0;}.label{width:${dims.w}in;height:${dims.h}in;page-break-after:always;padding:0.1in;text-align:center;box-sizing:border-box;display:flex;flex-direction:column;justify-content:center;align-items:center;font-family:Arial,sans-serif;}.barcode-img{max-width:${dims.w - 0.3}in;max-height:${dims.h - 0.8}in;}.sku{font-weight:bold;font-size:${dims.w >= 2 ? '12pt' : '10pt'};margin-top:0.05in;}.name{font-size:${dims.w >= 2 ? '10pt' : '8pt'};margin-top:0.03in;}.price{font-size:${dims.w >= 2 ? '11pt' : '9pt'};color:#2e7d32;margin-top:0.03in;}</style></head><body>`;
            for (const p of allLabels) {
                printContent += `<div class="label">
                    <img class="barcode-img" src="${p.barcodeDataUrl}" />
                    ${this.printSettings.showSku ? `<div class="sku">${p.sku}</div>` : ''}
                    ${this.printSettings.showProductName ? `<div class="name">${this.truncateText(p.product_name, 30)}</div>` : ''}
                    ${this.printSettings.showPrice ? `<div class="price">$${this.formatPriceNum(p.selling_price)}</div>` : ''}
                </div>`;
            }
            printContent += `<script>window.onload=function(){window.print();};<\/script></body></html>`;
            const printWindow = window.open();
            printWindow.document.write(printContent);
            printWindow.document.close();
        },
        async printViaDirect() {
            const dims = this.labelDimensions[this.printSettings.labelSize] || this.labelDimensions['1.5x2'];
            const allLabels = this.printAllPages;
            const iframe = document.createElement('iframe');
            iframe.style.position = 'absolute';
            iframe.style.width = '0px';
            iframe.style.height = '0px';
            iframe.style.border = 'none';
            document.body.appendChild(iframe);
            const iframeDoc = iframe.contentWindow.document;
            iframeDoc.open();
            iframeDoc.write(`<html><head><style>@page{size:${dims.w}in ${dims.h}in;margin:0.05in;}body{margin:0;padding:0;}.label{width:${dims.w}in;height:${dims.h}in;page-break-after:always;padding:0.1in;text-align:center;box-sizing:border-box;display:flex;flex-direction:column;justify-content:center;align-items:center;}</style></head><body>`);
            for (const p of allLabels) {
                iframeDoc.write(`<div class="label">
                    <img src="${p.barcodeDataUrl}" style="max-width:${dims.w - 0.3}in;max-height:${dims.h - 0.8}in;" />
                    ${this.printSettings.showSku ? `<div style="font-weight:bold;">${p.sku}</div>` : ''}
                </div>`);
            }
            iframeDoc.write('</body></html>');
            iframeDoc.close();
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        },
        truncateText(text, maxLength) {
            if (!text) return '';
            if (text.length <= maxLength) return text;
            return text.substring(0, maxLength - 3) + '...';
        },
        formatPriceNum(price) {
            if (!price && price !== 0) return '0.00';
            const num = parseFloat(price);
            return isNaN(num) ? '0.00' : num.toFixed(2);
        },
        
        // EXISTING METHODS (keep all your original methods here)
        showBulkImageUpload() {
            this.bulkImages = [];
            this.bulkImagePreview = [];
            this.$bvModal.show('modal-bulk-image-upload');
        },
        async handleBulkImageSelect(files) {
            this.bulkImages = [];
            this.bulkImagePreview = [];
            for (let file of files) {
                const fileName = file.name;
                const sku = fileName.replace(/\.[^/.]+$/, '');
                const base64 = await this.fileToBase64(file);
                this.bulkImages.push({
                    file: file,
                    sku: sku,
                    originalName: fileName,
                    preview: base64,
                    status: 'pending',
                    error: null
                });
                this.bulkImagePreview.push({
                    sku: sku,
                    preview: base64,
                    fileName: fileName
                });
            }
        },
        async processBulkImageUpload() {
            if (this.bulkImages.length === 0) {
                this.$bvToast.toast("Please select images to upload", { title: "Warning", autoHideDelay: 2000, variant: "warning" });
                return;
            }
            this.uploadingBulkImages = true;
            this.bulkImageProgress = { total: this.bulkImages.length, processed: 0, successful: 0, failed: 0, current: 0 };
            this.$bvModal.show('modal-bulk-image-progress');
            for (let i = 0; i < this.bulkImages.length; i++) {
                const imageItem = this.bulkImages[i];
                this.bulkImageProgress.current = i + 1;
                this.bulkImageProgress.processed = i;
                const product = this.allItems.find(p => p.sku === imageItem.sku || p.sku.toLowerCase() === imageItem.sku.toLowerCase());
                if (!product) {
                    imageItem.status = 'failed';
                    imageItem.error = `Product with SKU "${imageItem.sku}" not found`;
                    this.bulkImageProgress.failed++;
                    continue;
                }
                try {
                    const base64Image = await this.fileToBase64(imageItem.file);
                    const path = "/api/product/upload_product_image/";
                    const data = { product_sku: product.sku, product_id: product.id, image_data: base64Image };
                    const response = await new Promise((resolve, reject) => { Requestor.postSyncP(path, data, this, resolve, reject); });
                    if (response.data.error) throw new Error(response.data.error);
                    const updatedProduct = response.data.result;
                    this.updateProductInList(updatedProduct);
                    imageItem.status = 'success';
                    this.bulkImageProgress.successful++;
                } catch (error) {
                    imageItem.status = 'failed';
                    imageItem.error = error.message || "Upload failed";
                    this.bulkImageProgress.failed++;
                }
                this.bulkImageProgress.processed = i + 1;
                await new Promise(resolve => setTimeout(resolve, 500));
            }
            this.uploadingBulkImages = false;
            this.$bvToast.toast(`Bulk upload complete: ${this.bulkImageProgress.successful} successful, ${this.bulkImageProgress.failed} failed`, { title: "Bulk Upload Complete", variant: this.bulkImageProgress.failed === 0 ? "success" : "warning", autoHideDelay: 5000 });
            this.fetch_init(true, true);
        },
        resetBulkImageUpload() {
            this.bulkImages = [];
            this.bulkImagePreview = [];
            this.uploadingBulkImages = false;
            this.bulkImageProgress = { total: 0, processed: 0, successful: 0, failed: 0, current: 0 };
        },
        showLowInventoryAlert(product) {
            if (!product) {
                this.$bvToast.toast("Invalid product data", { title: "Error", variant: "danger", autoHideDelay: 3000 });
                return;
            }
            this.currentAlertProduct = product;
            if (product.low_inventory_alert_id) {
                this.alertConfig = {
                    alert_type: product.low_inventory_alert_type || 'threshold',
                    threshold_quantity: product.low_inventory_threshold || 10,
                    notification_methods: product.low_inventory_notification_methods || ['email', 'dashboard'],
                    alert_emails: product.low_inventory_alert_emails || '',
                    notes: product.low_inventory_notes || ''
                };
            } else if (product.properties && product.properties.low_inventory === true) {
                this.alertConfig = {
                    alert_type: 'threshold',
                    threshold_quantity: 5,
                    notification_methods: ['email', 'dashboard'],
                    alert_emails: '',
                    notes: 'Converted from legacy low inventory flag'
                };
            } else {
                this.alertConfig = {
                    alert_type: 'threshold',
                    threshold_quantity: 10,
                    notification_methods: ['email', 'dashboard'],
                    alert_emails: '',
                    notes: ''
                };
            }
            this.currentInventoryLevel = null;
            this.fetchCurrentInventory(product.id || product.product_id);
            this.showLowInventoryModal = true;
        },
        fetchCurrentInventory(productId) {
            if (!productId) {
                console.warn("No product ID provided for inventory fetch");
                this.currentInventoryLevel = null;
                return;
            }
            const path = "/api/inventory/get_product_inventory/";
            const data = { product_id: productId };
            const that = this;
            const successCallback = (response) => {
                if (response.data && response.data.result && response.data.result.current_stock !== undefined) {
                    that.currentInventoryLevel = response.data.result.current_stock;
                } else {
                    that.currentInventoryLevel = 0;
                }
            };
            const errorCallback = (error) => {
                console.error("Failed to fetch inventory:", error);
                that.currentInventoryLevel = null;
            };
            Requestor.postSyncP(path, data, this, successCallback, errorCallback);
        },
        resetLowInventoryModal() {
            this.alertConfig = { alert_type: 'threshold', threshold_quantity: 10, notification_methods: ['email', 'dashboard'], alert_emails: '', notes: '' };
            this.currentInventoryLevel = null;
            setTimeout(() => {
                if (!this.showLowInventoryModal) this.currentAlertProduct = null;
            }, 300);
        },
        saveLowInventoryAlert() {
            if (!this.currentAlertProduct) {
                this.$bvToast.toast("No product selected", { title: "Error", variant: "danger", autoHideDelay: 3000 });
                this.showLowInventoryModal = false;
                return;
            }
            if (!this.isAlertConfigValid) {
                this.$bvToast.toast("Please fill all required fields", { title: "Validation Error", variant: "warning", autoHideDelay: 3000 });
                return;
            }
            const alertData = {
                product_id: this.currentAlertProduct.id,
                product_sku: this.currentAlertProduct.sku,
                alert_type: this.alertConfig.alert_type,
                threshold_quantity: this.alertConfig.threshold_quantity,
                notification_methods: this.alertConfig.notification_methods,
                alert_emails: this.alertConfig.alert_emails,
                notes: this.alertConfig.notes,
                created_by: this.packet.userDetails.username
            };
            const path = "/api/product/set_low_inventory_alert/";
            const that = this;
            const successCallback = function(response) {
                if (response.data && response.data.error) {
                    that.$bvToast.toast(response.data.error, { title: "Error", variant: "danger", autoHideDelay: 3000 });
                } else {
                    that.$bvToast.toast(`Low inventory alert configured for ${that.currentAlertProduct.sku}`, { title: "Alert Set", variant: "success", autoHideDelay: 3000 });
                    const index = that.allItems.findIndex(p => p.id === that.currentAlertProduct.id);
                    if (index !== -1) {
                        that.allItems[index].low_inventory_enabled = true;
                        that.allItems[index].low_inventory_alert_id = response.data.result.id;
                        that.allItems[index].low_inventory_alert_type = alertData.alert_type;
                        that.allItems[index].low_inventory_threshold = alertData.threshold_quantity;
                        that.allItems[index].low_inventory_notification_methods = alertData.notification_methods;
                        that.allItems[index].low_inventory_alert_emails = alertData.alert_emails;
                        that.allItems[index].low_inventory_notes = alertData.notes;
                        that.allItems[index].low_inventory_effective_threshold = alertData.threshold_quantity;
                        that.allItems = [...that.allItems];
                        that.updateDisplayedItems();
                    }
                    that.showLowInventoryModal = false;
                    that.currentAlertProduct = null;
                }
            };
            const errorCallback = function(error) {
                console.error("Save alert error:", error);
                that.$bvToast.toast(error.message || "Failed to set low inventory alert", { title: "Error", variant: "danger", autoHideDelay: 3000 });
            };
            Requestor.postSyncP(path, alertData, this, successCallback, errorCallback);
        },
        hasLowInventoryAlert(item) {
            if (item.low_inventory_enabled) return true;
            if (item.properties && item.properties.low_inventory === true) return true;
            return false;
        },
        isLowInventoryLegacy(item) {
            if (!item.properties) return false;
            let properties = item.properties;
            if (typeof properties === 'string') {
                try { properties = JSON.parse(properties); } catch (e) { return false; }
            }
            if (properties && typeof properties === 'object') return properties.low_inventory === true;
            return false;
        },
        isLowInventoryTriggered(item) {
            if (item.is_low_inventory_triggered !== undefined) return item.is_low_inventory_triggered;
            const available = item.available || 0;
            const threshold = item.low_inventory_threshold || 5;
            return this.hasLowInventoryAlert(item) && available <= threshold;
        },
        getLowInventoryThreshold(item) {
            if (item.low_inventory_effective_threshold !== undefined && item.low_inventory_effective_threshold !== null) return item.low_inventory_effective_threshold;
            if (item.properties) {
                let properties = item.properties;
                if (typeof properties === 'string') {
                    try { properties = JSON.parse(properties); } catch (e) { return 5; }
                }
                if (properties.low_inventory_alert_config) return properties.low_inventory_alert_config.threshold_quantity || 5;
            }
            return 5;
        },
        getLowInventoryAlertType(item) {
            if (item.low_inventory_alert_type) return item.low_inventory_alert_type;
            if (item.properties) {
                let properties = item.properties;
                if (typeof properties === 'string') {
                    try { properties = JSON.parse(properties); } catch (e) { return 'threshold'; }
                }
                if (properties.low_inventory_alert_config) return properties.low_inventory_alert_config.alert_type || 'threshold';
            }
            return 'threshold';
        },
        downloadBarcodeImage(imageUrl, sku) {
            fetch(imageUrl).then(response => response.blob()).then(blob => {
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.href = url;
                link.download = `barcode_${sku}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }).catch(error => {
                console.error('Error downloading barcode:', error);
                this.$bvToast.toast("Error downloading barcode", { title: "Error", autoHideDelay: 2000, variant: "danger" });
            });
        },
        toggleBarcode() {
            this.ShowBarcode = !this.ShowBarcode;
        },
        handleBarcodeImageError(event) {
            event.target.style.display = 'none';
            const parent = event.target.parentElement;
            if (parent) {
                parent.innerHTML = `<div class="no-barcode-placeholder"><b-button size="sm" variant="outline-primary" onclick="this.closest('tr').__vueParentComponent.ctx.generateSingleBarcodeFromError(this)">Generate Barcode</b-button></div>`;
            }
        },
        generateSingleBarcode(item) {
            this.generatingSingleBarcode = true;
            let path = "/api/product/generate_single_barcode/";
            let data = { product_sku: item.sku, product_id: item.id, folder: "product_barcodes" };
            let that = this;
            let s = function(response) {
                that.generatingSingleBarcode = false;
                if (response.data.error) {
                    that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 3000, variant: "danger" });
                } else if (response.data.result) {
                    const barcodeUrl = response.data.result.barcode_url;
                    const index = that.allItems.findIndex(i => i.id === item.id || i.sku === item.sku);
                    if (index !== -1) {
                        that.allItems[index].barcode_image = barcodeUrl;
                        that.allItems = [...that.allItems];
                        that.updateDisplayedItems();
                    }
                    that.$bvToast.toast("Barcode generated successfully!", { title: "Success", autoHideDelay: 2000, variant: "success" });
                }
            };
            let f = function(error) {
                that.generatingSingleBarcode = false;
                console.error('Error generating barcode:', error);
                that.$bvToast.toast("Error generating barcode", { title: "Error", autoHideDelay: 3000, variant: "danger" });
            };
            Requestor.postAsyncP(path, data, this, s, f);
        },
        generateSingleBarcodeFromError(element) {
            const row = element.closest('tr');
            if (row && row.__vueParentComponent.ctx.item) this.generateSingleBarcode(row.__vueParentComponent.ctx.item);
        },
        showFullBarcode(item) {
            if (item.barcode_image) {
                this.$bvModal.show('modal-barcode-viewer');
                this.$nextTick(() => {
                    this.currentBarcodeImage = item.barcode_image;
                    this.currentProductName = item.product_name || item.sku;
                    this.currentProductSku = item.sku;
                });
            } else {
                this.$bvToast.toast("No barcode available for this product", { title: "Info", autoHideDelay: 2000, variant: "info" });
            }
        },
        generateAllBarcodes() {
            if (!confirm('This will generate barcodes for ALL products in the database that don\'t have one. This may take several minutes. Continue?')) return;
            this.generatingBarcodes = true;
            this.barcodeProgress = { total: 0, processed: 0, successful: 0, failed: 0, percentage: 0, message: 'Starting barcode generation...', errors: [] };
            this.$bvModal.show('modal-barcode-progress');
            let path = "/api/product/generate_barcodes_for_all_skus/";
            let data = { batch_size: 50, skip_existing: true, folder: "product_barcodes", user_details: this.packet.userDetails };
            let that = this;
            let s = function(response) {
                that.generatingBarcodes = false;
                if (response.data.error) {
                    that.$bvToast.toast(response.data.error, { title: "Error", variant: "danger", autoHideDelay: 3000 });
                    that.barcodeProgress.message = 'Error: ' + response.data.error;
                } else {
                    const result = response.data.result;
                    that.barcodeProgress.total = result.total;
                    that.barcodeProgress.processed = result.total;
                    that.barcodeProgress.successful = result.successful;
                    that.barcodeProgress.failed = result.failed;
                    that.barcodeProgress.percentage = 100;
                    that.barcodeProgress.errors = result.errors || [];
                    that.barcodeProgress.message = `Completed! Generated ${result.successful} barcodes in ${result.duration_seconds} seconds`;
                    that.$bvToast.toast(`Generated ${result.successful} barcodes successfully. ${result.failed} failed.`, { title: "Barcode Generation Complete", variant: result.failed === 0 ? "success" : "warning", autoHideDelay: 5000 });
                    that.fetch_init(true, true);
                }
            };
            let f = function(error) {
                that.generatingBarcodes = false;
                console.error('Error generating barcodes:', error);
                that.$bvToast.toast("Error starting barcode generation. Check server logs.", { title: "Error", variant: "danger", autoHideDelay: 3000 });
                that.barcodeProgress.message = 'Error: Failed to start generation';
            };
            Requestor.postAsyncP(path, data, this, s, f);
        },
        toggleSelectionMode() {
            this.showSelectionMode = !this.showSelectionMode;
            if (!this.showSelectionMode) this.clearSelection();
        },
        clearSelection() {
            this.selectedProducts = [];
            this.selectAll = false;
        },
        toggleSelectAll(checked) {
            if (checked) this.selectedProducts = this.filtered_final.map(item => item.sku);
            else this.selectedProducts = [];
        },
        updateSelectAll() {
            this.selectAll = this.selectedProducts.length === this.filtered_final.length;
        },
        openBulkStatusModal(action) {
            this.bulkStatusAction = action;
            this.bulkStatusModalVisible = true;
            this.$bvModal.show('modal-bulk-status');
        },
        openBulkDiscontinuedModal(action) {
            this.bulkDiscontinuedAction = action;
            this.discontinuedReason = '';
            this.updateChildrenDiscontinued = false;
            this.discontinuedModalVisible = true;
            this.$bvModal.show('modal-bulk-discontinued');
        },
        executeBulkDiscontinuedChange() {
            if (this.selectedProducts.length === 0) {
                this.$bvToast.toast("No products selected", { 
                    title: "Warning", 
                    autoHideDelay: 1500, 
                    variant: "warning" 
                });
                return;
            }
            
            this.isLoadingMore = true;
            
            let path = "/api/product/change_bulk_product_status/";
            let data = {
                product_skus: this.selectedProducts,
                discontinued: this.bulkDiscontinuedAction,
                update_children: this.updateChildrenDiscontinued || false,
                user_details: {
                    username: this.packet.userDetails.username,
                    user_id: this.packet.userDetails.user_id
                }
            };
            
            let that = this;
            let s = function(response) {
                that.isLoadingMore = false;
                if (response.data.error) {
                    that.$bvToast.toast(response.data.error, { 
                        title: "Error", 
                        autoHideDelay: 3000, 
                        variant: "danger" 
                    });
                } else {
                    let message = `Successfully ${that.bulkDiscontinuedAction ? 'discontinued' : 'restored'} ${response.data.result.updated_count || that.selectedProducts.length} products`;
                    if (response.data.result.skipped_count) {
                        message += ` (${response.data.result.skipped_count} skipped)`;
                    }
                    that.$bvToast.toast(message, { 
                        title: "Success", 
                        autoHideDelay: 2000, 
                        variant: "success" 
                    });
                    that.clearSelection();
                    that.showSelectionMode = false;
                    that.fetch_init(true, true);
                }
            };
            
            let f = function(error) {
                that.isLoadingMore = false;
                console.error('Error in bulk discontinued change:', error);
                that.$bvToast.toast("Error updating discontinued status", { 
                    title: "Error", 
                    autoHideDelay: 3000, 
                    variant: "danger" 
                });
            };
            
            Requestor.postAsyncP(path, data, this, s, f);
            this.$bvModal.hide('modal-bulk-discontinued');
        },
        applyDiscontinuedFilter() {
            if (this.discontinuedFilter) {
                this.currentOffset = 0;
                this.hasMore = true;
                this.allItems = [];
                this.displayedItems = [];
                this.fetchProducts(0, this.pageSize, false);
            } else {
                this.fetch_init(true);
            }
        },
        executeBulkStatusChange() {
            if (this.selectedProducts.length === 0) {
                this.$bvToast.toast("No products selected", { title: "Warning", autoHideDelay: 1500, variant: "warning" });
                return;
            }
            this.isLoadingMore = true;
            this.changeBulkProductStatus(this.selectedProducts, this.bulkStatusAction);
            this.$bvModal.hide('modal-bulk-status');
            this.bulkStatusModalVisible = false;
        },
        changeBulkProductStatus(productSkus, newStatus) {
            let path = "/api/product/change_bulk_product_status/";
            let data = {
                product_skus: productSkus,
                status: newStatus,
                update_children: this.updateChildren || false,
                user_details: { username: this.packet.userDetails.username, user_id: this.packet.userDetails.user_id }
            };
            let that = this;
            let s = function(response) {
                that.isLoadingMore = false;
                if (response.data.error) {
                    that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 3000, variant: "danger" });
                } else {
                    let message = `Successfully updated ${response.data.result.updated_count || productSkus.length} products`;
                    if (response.data.result.skipped_count) message += ` (${response.data.result.skipped_count} skipped)`;
                    that.$bvToast.toast(message, { title: "Success", autoHideDelay: 2000, variant: "success" });
                    that.clearSelection();
                    that.showSelectionMode = false;
                    that.fetch_init(true, true);
                }
            };
            let f = function(error) {
                that.isLoadingMore = false;
                console.error('Error in bulk status change:', error);
                that.$bvToast.toast("Error updating product statuses", { title: "Error", autoHideDelay: 3000, variant: "danger" });
            };
            Requestor.postAsyncP(path, data, this, s, f);
        },
        applyItemStatusFilter() {
            this.currentOffset = 0;
            this.hasMore = true;
            this.allItems = [];
            this.displayedItems = [];
            this.fetchProducts(0, this.pageSize, false);
        },
        isOldProduct(item) {
            return (item.sold_so_far && item.sold_so_far > 0) || (item.received && item.received > 0) || (item.last_sale_date && moment().diff(moment(item.last_sale_date), 'days') > 30) || (item.created_at && moment().diff(moment(item.created_at), 'days') > 30) || (item.first_received_date && moment().diff(moment(item.first_received_date), 'days') > 30);
        },
        toggleimage() {
            this.Showimage = !this.Showimage;
            if (!this.showSimplifiedColumns) this.applyColumnVisibility();
        },
        toggleMainColumns() {
            this.showMainColumns = !this.showMainColumns;
            if (!this.showSimplifiedColumns) this.applyColumnVisibility();
        },
        togglePriceColumns() {
            this.showPriceColumns = !this.showPriceColumns;
            if (!this.showSimplifiedColumns) this.applyColumnVisibility();
        },
        toggleDiscountColumns() {
            this.showDiscountColumns = !this.showDiscountColumns;
            if (!this.showSimplifiedColumns) this.applyColumnVisibility();
        },
        toggleDetailColumns() {
            this.showDetailColumns = !this.showDetailColumns;
            if (!this.showSimplifiedColumns) this.applyColumnVisibility();
        },
        applyColumnVisibility() {
            console.log('Column visibility updated');
        },
        applyDetailFilters() {
            console.log("Applying detail filters");
            this.currentOffset = 0;
            this.hasMore = true;
            this.allItems = [];
            this.displayedItems = [];
            this.fetchProducts(0, this.pageSize, false);
        },
        resetDetailFilters() {
            this.typeFilter = null;
            this.colorFilter = null;
            this.sizeFilter = null;
            this.materialFilter = null;
            this.functionalityFilter = null;
        },
        fetchAllHierarchyOptions(categoryId = null) {
            let path = "/api/product/get_hierarchy_options/";
            let data = categoryId ? { category_id: categoryId } : {};
            let that = this;
            let s = function(response) {
                if (response.data && !response.data.error && response.data.result) {
                    const options = response.data.result;
                    that.typeOptions = [{ value: null, text: 'ALL TYPES' }, ...(options.types || []).map(type => ({ value: type.id, text: type.type_name || type.name || 'Unknown' }))];
                    that.colorOptions = [{ value: null, text: 'ALL COLORS' }, ...(options.colors || []).map(color => ({ value: color.id, text: color.color_name || color.name || 'Unknown' }))];
                    that.sizeOptions = [{ value: null, text: 'ALL SIZES' }, ...(options.sizes || []).map(size => ({ value: size.id, text: size.size_name || size.name || 'Unknown' }))];
                    that.materialOptions = [{ value: null, text: 'ALL MATERIALS' }, ...(options.materials || []).map(material => ({ value: material.id, text: material.material_name || material.name || 'Unknown' }))];
                    that.functionalityOptions = [{ value: null, text: 'ALL FUNCTIONALITIES' }, ...(options.functionalities || []).map(func => ({ value: func.id, text: func.functionality_name || func.name || 'Unknown' }))];
                    that.subcategories = options.subcategories || [];
                    that.packet.subcategoriesdict = that.subcategories.reduce((map, obj) => { map[obj.id] = obj.subcategory || obj.name; return map; }, {});
                } else {
                    that.fetchFilterOptions();
                }
            };
            let f = function(error) { that.fetchFilterOptions(); };
            Requestor.getSyncP(path, data, this, s, f);
        },
        applySlowestSellingFilter() {
            if (!this.slowestSellingFilter) {
                this.currentOffset = 0;
                this.hasMore = false;
                this.allItems = [];
                this.displayedItems = [];
                this.updateDisplayedItems();
                return;
            }
            this.bestSellingFilter = null;
            this.items_being_loaded = true;
            let path = "/api/product/get_slowest_selling_products/";
            let data = { time_period: this.slowestSellingFilter, limit: 1000, user_role: this.packet.userDetails.userrole, user_location_id: this.user_location_id };
            let that = this;
            let s = function(response) {
                that.items_being_loaded = false;
                if (response.data.error) {
                    that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                } else {
                    that.allItems = response.data.result.map(item => ({ ...item, edit: false, available: item.available || 0, in_stock: item.in_stock || 0, reserved: item.reserved || 0, missing: 0, slow_score: item._slow_score || 0, slow_ratio: item._ratio || 0, slow_period: item._period || '' }));
                    that.updateDisplayedItems();
                    that.totalItemsCount = that.allItems.length;
                    that.hasMore = false;
                }
            };
            let f = function(error) {
                that.items_being_loaded = false;
                console.error('Error fetching slowest selling products:', error);
                that.$bvToast.toast("Error fetching slowest selling products", { title: "Error", autoHideDelay: 1500, variant: "danger" });
            };
            Requestor.getSyncP(path, data, this, s, f);
        },
        applySlowestSellingSort(items) {
            const filter = this.slowestSellingFilter;
            let sortedItems = [...items];
            sortedItems = sortedItems.map(item => {
                let received = 0, sold = 0;
                switch (filter) {
                    case 'slowest_7_days': received = item.received_7d || 0; sold = item.sold_7d || 0; break;
                    case 'slowest_30_days': received = item.received_30d || 0; sold = item.sold_30d || 0; break;
                    case 'slowest_60_days': received = item.received_60d || 0; sold = item.sold_60d || 0; break;
                    case 'slowest_90_days': received = item.received_90d || 0; sold = item.sold_90d || 0; break;
                    case 'slowest_all_time': received = item.received || 0; sold = item.sold_so_far || 0; break;
                }
                let score = 0;
                if (received > 0) {
                    if (sold === 0) score = received * 1000;
                    else score = received / sold;
                }
                return { ...item, _slowScore: score, _received: received, _sold: sold };
            });
            sortedItems.sort((a, b) => b._slowScore - a._slowScore);
            return sortedItems;
        },
        applyBestSellingFilter() {
            if (!this.bestSellingFilter) {
                this.currentOffset = 0;
                this.hasMore = false;
                this.allItems = [];
                this.displayedItems = [];
                this.updateDisplayedItems();
                return;
            }
            this.slowestSellingFilter = null;
            this.items_being_loaded = true;
            let path = "/api/product/get_best_selling_products/";
            let data = { time_period: this.bestSellingFilter, limit: 1000, user_role: this.packet.userDetails.userrole, user_location_id: this.user_location_id };
            let that = this;
            let s = function(response) {
                that.items_being_loaded = false;
                if (response.data.error) {
                    that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                } else {
                    that.allItems = response.data.result.map(item => ({ ...item, edit: false, available: item.available || 0, in_stock: item.in_stock || 0, reserved: item.reserved || 0, missing: 0 }));
                    that.updateDisplayedItems();
                    that.totalItemsCount = that.allItems.length;
                    that.hasMore = false;
                    that.filtered_final = that.applyBestSellingSort(that.displayedItems);
                }
            };
            let f = function(error) {
                that.items_being_loaded = false;
                console.error('Error fetching best selling products:', error);
                that.$bvToast.toast("Error fetching best selling products", { title: "Error", autoHideDelay: 1500, variant: "danger" });
            };
            Requestor.getSyncP(path, data, this, s, f);
        },
        applyBestSellingSort(items) {
            const filter = this.bestSellingFilter;
            let sortedItems = [...items];
            switch (filter) {
                case '7_days': sortedItems.sort((a, b) => { const aSold = a.sold_7d || 0; const bSold = b.sold_7d || 0; return this.bestSellingSortDesc ? bSold - aSold : aSold - bSold; }); break;
                case '30_days': sortedItems.sort((a, b) => { const aSold = a.sold_30d || 0; const bSold = b.sold_30d || 0; return this.bestSellingSortDesc ? bSold - aSold : aSold - bSold; }); break;
                case '60_days': sortedItems.sort((a, b) => { const aSold = a.sold_60d || 0; const bSold = b.sold_60d || 0; return this.bestSellingSortDesc ? bSold - aSold : aSold - bSold; }); break;
                case '90_days': sortedItems.sort((a, b) => { const aSold = a.sold_90d || 0; const bSold = b.sold_90d || 0; return this.bestSellingSortDesc ? bSold - aSold : aSold - bSold; }); break;
                case 'all_time': sortedItems.sort((a, b) => { const aSold = a.sold_so_far || 0; const bSold = b.sold_so_far || 0; return this.bestSellingSortDesc ? bSold - aSold : aSold - bSold; }); break;
                case 'profit_margin': sortedItems.sort((a, b) => { const aMargin = this.calculateProfitMargin(a.landed_cost, a.selling_price); const bMargin = this.calculateProfitMargin(b.landed_cost, b.selling_price); return this.bestSellingSortDesc ? bMargin - aMargin : aMargin - bMargin; }); break;
            }
            return sortedItems;
        },
        calculateProfitMargin(landedCost, sellingPrice) {
            if (!landedCost || !sellingPrice || landedCost <= 0 || sellingPrice <= 0) return 0;
            const profit = sellingPrice - landedCost;
            const margin = (profit / sellingPrice) * 100;
            return Math.round(margin * 10) / 10;
        },
        async fetchProducts(offset = 0, limit = null, append = false) {
            if (this.isLoadingMore && append) return;
            this.isLoadingMore = true;
            if (!append) this.items_being_loaded = true;
            try {
                let path = "/api/product/get_products_list_v3/";
                let data = {
                    location_id: this.location_id,
                    user_role: this.packet.userDetails.userrole,
                    user_location_id: this.user_location_id,
                    offset: offset,
                    limit: limit || this.pageSize
                };
                
                // Add discontinued filter
                if (this.discontinuedFilter) {
                    data.discontinued = this.discontinuedFilter === 'discontinued';
                }
                
                // Add new arrival filter
                if (this.newArrivalFilter) {
                    data.is_new_arrival = this.newArrivalFilter === 'new';
                }

                // Add received status filter
                if (this.receivedStatusFilter === 'recently_received') {
                    data.recently_received = true;
                } else if (this.receivedStatusFilter === 'not_recently_received') {
                    data.recently_received = false;
                }
                
                if (this.typeFilter) data.type_id = this.typeFilter;
                if (this.colorFilter) data.color_id = this.colorFilter;
                if (this.sizeFilter) data.size_id = this.sizeFilter;
                if (this.materialFilter) data.material_id = this.materialFilter;
                if (this.functionalityFilter) data.functionality_id = this.functionalityFilter;
                if (this.itemTypeFilter === 'single') data.is_parent = false;
                else if (this.itemTypeFilter === 'group') data.is_parent = true;
                if (this.subcategory_id) data.subcategory_id = this.subcategory_id;
                if (this.category_id) data.category_id = this.category_id;
                if (this.vendor_id) data.vendor_id = this.vendor_id;
                if (this.product_query) data.product_query = this.product_query;
                if (this.statusFilter !== 'all') data.status = this.statusFilter;
                
                const response = await new Promise((resolve, reject) => { Requestor.getSyncP(path, data, this, resolve, reject); });
                if (response.data.result) {
                    const newItems = response.data.result.map(item => {
                        const expectedAvailable = (item.in_stock || 0) - (item.reserved || 0);
                        const missing = Math.max(0, expectedAvailable - (item.available || 0));
                        return {
                            ...item,
                            edit: false,
                            quantity_per_box: item.quantity_per_box || (item.properties && item.properties.quantity_per_box) || 1,
                            wholesale_price: item.wholesale_price || 0,
                            landed_cost: item.landed_cost || 0,
                            spiff: item.spiff || 0,
                            cubes: item.cubes || 0,
                            in_stock: item.in_stock || 0,
                            reserved: item.reserved || 0,
                            available: item.available || 0,
                            missing: missing,
                            sold_7d: item.sold_7d || 0,
                            sold_30d: item.sold_30d || 0,
                            sold_60d: item.sold_60d || 0,
                            received_7d: item.received_7d || 0,
                            received_30d: item.received_30d || 0,
                            received_60d: item.received_60d || 0,
                            scheduled_7d: item.scheduled_7d || 0,
                            scheduled_30d: item.scheduled_30d || 0,
                            scheduled_60d: item.scheduled_60d || 0,
                            special_discount_percent: item.special_discount_percent || 10,
                            special_discount_end_date: item.special_discount_end_date || null,
                            image_url: item.image_url || null,
                            discontinued: item.discontinued || false,
                            discontinued_date: item.discontinued_date || null,
                            is_new_arrival: item.is_new_arrival || false,
                            new_arrival_expiry_date: item.new_arrival_expiry_date || null,
                            recently_received: item.recently_received || false,
                        };
                    });
                    if (append) this.allItems = [...this.allItems, ...newItems];
                    else { this.allItems = newItems; this.totalItemsCount = newItems.length; }
                    this.updateDisplayedItems();
                    this.hasMore = newItems.length === this.pageSize;
                    this.currentOffset = offset + newItems.length;
                    if (!append) this.totalItemsCount = this.allItems.length;
                }
            } catch (error) {
                console.error('Error fetching products:', error);
                this.$bvToast.toast("Error fetching products list", { title: "Error", autoHideDelay: 1500, variant: "danger" });
            } finally {
                this.items_being_loaded = false;
                this.isLoadingMore = false;
            }
        },
        getSubcategoryLabel(subcategoryId) {
            if (!subcategoryId) return '';
            const subcategory = this.subcategories.find(sub => sub.id === subcategoryId);
            return subcategory ? subcategory.subcategory : subcategoryId;
        },
        updateDisplayedItems() {
            this.displayedItems = this.allItems;
        },
        async loadMore() {
            if (this.isLoadingMore || !this.hasMore) return;
            await this.fetchProducts(this.currentOffset, this.pageSize, true);
        },
        handleScroll(event) {
            const container = event.target;
            if (container.scrollTop === this.lastScrollTop) return;
            this.lastScrollTop = container.scrollTop;
            const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
            if (scrollBottom < 100 && !this.isLoadingMore && this.hasMore) this.loadMore();
        },
        async fetch_init(first, forceFetch = false) {
            if (first) {
                this.vendor_id = null;
                this.category_id = null;
                this.subcategory_id = null;
                this.product_query = null;
                this.bestSellingFilter = null;
                this.slowestSellingFilter = null;
                this.statusFilter = 'active';
                this.itemTypeFilter = null;
                this.itemStatusFilter = null;
                this.discontinuedFilter = null;
                this.newArrivalFilter = null;
                this.receivedStatusFilter = null;
                this.resetDetailFilters();
                if (this.packet.userDetails.userrole === 'admin') this.location_id = null;
                else this.location_id = this.user_location_id;
            }
            if (first || this.hasActiveFilters || forceFetch) {
                this.currentOffset = 0;
                this.hasMore = true;
                this.allItems = [];
                this.displayedItems = [];
                await this.fetchProducts(0, this.pageSize, false);
            } else {
                this.currentOffset = 0;
                this.hasMore = false;
                this.allItems = [];
                this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        async fetch_category() {
            this.resetDetailFilters();
            this.showCategoryStats = false;
            if (this.category_id) {
                this.subcategory_id = null;
                this.currentOffset = 0;
                this.hasMore = true;
                this.allItems = [];
                this.displayedItems = [];
                await this.fetchProducts(0, this.pageSize, false);
            } else {
                await this.fetch_init(true);
            }
        },
        async fetch_vendor() {
            if (this.vendor_id) {
                this.currentOffset = 0;
                this.hasMore = true;
                this.allItems = [];
                this.displayedItems = [];
                await this.fetchProducts(0, this.pageSize, false);
            } else {
                await this.fetch_init(true);
            }
        },
        applyStatusFilter() {
            if (this.statusFilter === 'not-active') this.statusFilter = 'notactive';
            if (this.statusFilter !== 'all') this.fetch_init(false, true);
            else this.fetch_init(true);
        },
        applySpiffFilter() {
            if (this.spiffFilter) {
                this.currentOffset = 0;
                this.hasMore = true;
                this.allItems = [];
                this.displayedItems = [];
                this.fetchProducts(0, this.pageSize, false);
            } else {
                this.currentOffset = 0;
                this.hasMore = false;
                this.allItems = [];
                this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        applyReceivedStatusFilter() {
            if (this.receivedStatusFilter) {
                this.currentOffset = 0;
                this.hasMore = true;
                this.allItems = [];
                this.displayedItems = [];
                this.fetchProducts(0, this.pageSize, false);
            } else {
                this.currentOffset = 0;
                this.hasMore = false;
                this.allItems = [];
                this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        async fetch_location() {
            if (this.packet.userDetails.userrole !== 'admin') {
                this.$bvToast.toast("Only administrators can filter by location", { title: "Access Denied", autoHideDelay: 1500, variant: "warning" });
                this.location_id = this.user_location_id;
                return;
            }
            if (this.location_id) {
                this.currentOffset = 0;
                this.hasMore = true;
                this.allItems = [];
                this.displayedItems = [];
                await this.fetchProducts(0, this.pageSize, false);
            } else {
                this.currentOffset = 0;
                this.hasMore = false;
                this.allItems = [];
                this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        async fetch_product_name_sku() {
            if (this.product_query && this.product_query.length > 0) {
                this.currentOffset = 0;
                this.hasMore = true;
                this.allItems = [];
                this.displayedItems = [];
                await this.fetchProducts(0, this.pageSize, false);
            } else {
                this.currentOffset = 0;
                this.hasMore = false;
                this.allItems = [];
                this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        showCsvUpload() {
            this.csvFile = null;
            this.csvPreview = [];
            this.$bvModal.show('modal-csv-upload');
        },
        clearCsvUpload() {
            this.csvFile = null;
            this.csvPreview = [];
            this.$bvModal.hide('modal-csv-upload');
        },
        onFileSelect(file) {
            if (!file) { this.csvPreview = []; return; }
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const csvText = e.target.result;
                    const lines = csvText.split('\n');
                    if (lines.length < 2) {
                        this.$bvToast.toast("CSV file is empty or invalid", { title: "Error", autoHideDelay: 2000, variant: "danger" });
                        return;
                    }
                    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
                    const expectedHeaders = ['sku', 'description', 'category', 'sub category', 'vendor', 'qty per box', 'fob price', 'landed cst', 'whlsl price', 'sell price', 'cubes', 'spiff'];
                    if (headers.length !== expectedHeaders.length) {
                        this.$bvToast.toast(`Expected ${expectedHeaders.length} columns but found ${headers.length}`, { title: "Error", autoHideDelay: 2000, variant: "danger" });
                        return;
                    }
                    this.csvPreview = [];
                    for (let i = 1; i < Math.min(35, lines.length); i++) {
                        if (lines[i].trim()) {
                            const values = this.parseCSVLine(lines[i]);
                            if (values.length === expectedHeaders.length) {
                                this.csvPreview.push({
                                    sku: values[0], description: values[1], category: values[2], sub_category: values[3], vendor: values[4],
                                    quantity_per_box: values[5], fob_price: values[6], landed_cost: values[7], wholesale_price: values[8],
                                    selling_price: values[9], cubes: values[10], spiff: values[11]
                                });
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error parsing CSV:', error);
                    this.$bvToast.toast("Error parsing CSV file", { title: "Error", autoHideDelay: 2000, variant: "danger" });
                }
            };
            reader.readAsText(file);
        },
        parseCSVLine(line) {
            const result = [];
            let current = '';
            let inQuotes = false;
            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                if (char === '"') inQuotes = !inQuotes;
                else if (char === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
                else current += char;
            }
            result.push(current.trim());
            return result;
        },
        async processCsvUpload() {
            if (!this.csvFile) {
                this.$bvToast.toast("Please select a CSV file first", { title: "Warning", autoHideDelay: 1500, variant: "warning" });
                return;
            }
            this.uploadingCsv = true;
            try {
                const csvText = await this.readFileAsText(this.csvFile);
                const lines = csvText.split('\n');
                const products = [];
                for (let i = 1; i < lines.length; i++) {
                    if (lines[i].trim()) {
                        const values = this.parseCSVLine(lines[i]);
                        if (values.length === 12) {
                            const categoryId = this.findCategoryId(values[2]);
                            const subcategoryId = this.findSubcategoryId(values[3], categoryId);
                            const vendorId = this.findVendorId(values[4]);
                            const product = {
                                product_sku: values[0].replace(/\s/g, ''),
                                product_name: values[1],
                                category_id: categoryId,
                                subcategory_id: subcategoryId,
                                vendor_id: vendorId,
                                quantity_per_box: parseInt(values[5]) || 1,
                                fob_price: parseFloat(values[6]) || 0,
                                landed_cost: parseFloat(values[7]) || 0,
                                wholesale_price: parseFloat(values[8]) || 0,
                                selling_price: parseFloat(values[9]) || 0,
                                cubes: parseFloat(values[10]) || 0,
                                spiff: parseFloat(values[11]) || 0,
                                properties: { spiff: parseFloat(values[11]) || 0, cubes: parseFloat(values[10]) || 0, quantity_per_box: parseInt(values[5]) || 1 }
                            };
                            if (product.product_sku && product.product_name && product.category_id && product.vendor_id) products.push(product);
                        }
                    }
                }
                if (products.length === 0) throw new Error('No valid products found in CSV. Check that columns match expected format and categories/vendors exist in the system.');
                await this.uploadProductsBatch(products);
            } catch (error) {
                console.error('Error processing CSV:', error);
                this.$bvToast.toast(error.message || "Error processing CSV file", { title: "Error", autoHideDelay: 2000, variant: "danger" });
            } finally {
                this.uploadingCsv = false;
            }
        },
        findSubcategoryId(subcategoryName, categoryId) {
            if (!subcategoryName || !categoryId) return null;
            const cleanSubcategoryName = subcategoryName.trim().toLowerCase();
            const subcategory = this.subcategories.find(subcat => subcat.category_id == categoryId && subcat.subcategory.trim().toLowerCase() === cleanSubcategoryName);
            return subcategory ? subcategory.id : null;
        },
        readFileAsText(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = e => resolve(e.target.result);
                reader.onerror = reject;
                reader.readAsText(file);
            });
        },
        findCategoryId(categoryName) {
            const category = this.packet.categories.find(cat => cat.text.toLowerCase().includes(categoryName.toLowerCase()) || categoryName.toLowerCase().includes(cat.text.toLowerCase()));
            return category ? category.value : null;
        },
        findVendorId(vendorName) {
            const cleanVendorName = vendorName.trim().toLowerCase();
            let vendor = this.packet.vendors.find(vend => vend.text.trim().toLowerCase() === cleanVendorName);
            if (!vendor) return this.handleVendorMatching(vendorName, cleanVendorName);
            return vendor.value;
        },
        handleVendorMatching(vendorName, cleanVendorName) {
            const vendorMappings = { 'global': 'GLOBAL', 'global trading': 'GLOBAL TRADING' };
            const mappedVendor = vendorMappings[cleanVendorName];
            if (mappedVendor) {
                const vendor = this.packet.vendors.find(vend => vend.text.trim().toLowerCase() === mappedVendor.toLowerCase());
                return vendor ? vendor.value : null;
            }
            return null;
        },
        uploadProductsBatch(products) {
            return new Promise((resolve, reject) => {
                const path = "/api/product/add_batch_products/";
                const data = { items: products };
                Requestor.postSyncP(path, data, this, (response) => {
                    if (response.data.error) reject(new Error(response.data.error));
                    else {
                        this.$bvToast.toast(`Successfully uploaded ${products.length} products`, { title: "Success", autoHideDelay: 2000, variant: "success" });
                        this.clearCsvUpload();
                        this.fetch_init(true);
                        resolve(response.data);
                    }
                }, (error) => { reject(error); });
            });
        },
        async downloadCSV(downloadAll = false) {
            try {
                this.downloadingExcel = true;
                let dataToExport;
                if (downloadAll) {
                    const response = await this.fetchExportData();
                    dataToExport = response.data.result || [];
                } else dataToExport = this.filtered_final;
                if (dataToExport.length === 0) {
                    this.$bvToast.toast("No data available to export", { title: "Warning", autoHideDelay: 1500, variant: "warning" });
                    return;
                }
                this.generateCSVFile(dataToExport, downloadAll ? 'all' : 'current');
            } catch (error) {
                console.error('Error exporting to CSV:', error);
                this.$bvToast.toast("Error exporting to CSV", { title: "Error", autoHideDelay: 2000, variant: "danger" });
            } finally {
                this.downloadingExcel = false;
            }
        },
        async downloadByCategory() {
            if (!this.category_id) {
                this.$bvToast.toast("Please select a category first", { title: "Warning", autoHideDelay: 1500, variant: "warning" });
                return;
            }
            try {
                this.downloadingExcel = true;
                let path = "/api/product/get_products_list_v3/";
                let data = { category_id: this.category_id, limit: 10000, user_role: this.packet.userDetails.userrole, user_location_id: this.user_location_id };
                const response = await new Promise((resolve, reject) => { Requestor.getSyncP(path, data, this, resolve, reject); });
                if (response.data.result) this.generateCSVFile(response.data.result, 'category');
                else throw new Error(response.data.error || 'Failed to fetch category data');
            } catch (error) {
                console.error('Error exporting category to CSV:', error);
                this.$bvToast.toast("Error exporting category data to CSV", { title: "Error", autoHideDelay: 2000, variant: "danger" });
            } finally {
                this.downloadingExcel = false;
            }
        },
        async downloadByVendor() {
            if (!this.vendor_id) {
                this.$bvToast.toast("Please select a vendor first", { title: "Warning", autoHideDelay: 1500, variant: "warning" });
                return;
            }
            try {
                this.downloadingExcel = true;
                let path = "/api/product/get_products_list_v3/";
                let data = { vendor_id: this.vendor_id, limit: 10000, user_role: this.packet.userDetails.userrole, user_location_id: this.user_location_id };
                const response = await new Promise((resolve, reject) => { Requestor.getSyncP(path, data, this, resolve, reject); });
                if (response.data.result) this.generateCSVFile(response.data.result, 'vendor');
                else throw new Error(response.data.error || 'Failed to fetch vendor data');
            } catch (error) {
                console.error('Error exporting vendor to CSV:', error);
                this.$bvToast.toast("Error exporting vendor data to CSV", { title: "Error", autoHideDelay: 2000, variant: "danger" });
            } finally {
                this.downloadingExcel = false;
            }
        },
        fetchExportData() {
            return new Promise((resolve, reject) => {
                let path = "/api/product/get_products_list_v3/";
                let data = { location_id: this.location_id, user_role: this.packet.userDetails.userrole, user_location_id: this.user_location_id, limit: 10000 };
                if (this.category_id) data.category_id = this.category_id;
                if (this.vendor_id) data.vendor_id = this.vendor_id;
                if (this.product_query) data.product_query = this.product_query;
                if (this.statusFilter !== 'all') data.status = this.statusFilter;
                if (this.discontinuedFilter) data.discontinued = this.discontinuedFilter === 'discontinued';
                if (this.newArrivalFilter) data.is_new_arrival = this.newArrivalFilter === 'new';
                if (this.receivedStatusFilter === 'recently_received') data.recently_received = true;
                else if (this.receivedStatusFilter === 'not_recently_received') data.recently_received = false;
                Requestor.getSyncP(path, data, this, resolve, reject);
            });
        },
        formatDateTime(dateString) {
            if (!dateString) return '';
            return moment(dateString).format('YYYY-MM-DD HH:mm:ss');
        },
        generateCSVFile(exportData, type = 'current') {
            const csvData = this.prepareExportData(exportData);
            const headers = Object.keys(csvData[0] || {});
            let csvContent = headers.join(',') + '\n';
            csvData.forEach(row => {
                const rowData = headers.map(header => {
                    let value = row[header];
                    if (value === 0 || value === '0') return '0';
                    if (typeof value === 'number') return value.toString();
                    if (value === null || value === undefined || value === '') return '';
                    value = value.toString();
                    if (value.includes(',') || value.includes('"') || value.includes('\n')) value = '"' + value.replace(/"/g, '""') + '"';
                    return value;
                });
                csvContent += rowData.join(',') + '\n';
            });
            const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
            let filename = `products_export_${timestamp}`;
            if (type === 'all') filename += '_all_filtered';
            else if (type === 'category') filename += '_category';
            else if (type === 'vendor') filename += '_vendor';
            if (this.category_id && this.packet.categoriesdict[this.category_id]) filename += `_${this.packet.categoriesdict[this.category_id].replace(/[^a-zA-Z0-9]/g, '_')}`;
            if (this.vendor_id && this.packet.vendorsdict[this.vendor_id]) filename += `_${this.packet.vendorsdict[this.vendor_id].replace(/[^a-zA-Z0-9]/g, '_')}`;
            filename += '.csv';
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            this.$bvToast.toast(`Exported ${exportData.length} products to CSV`, { title: "Success", autoHideDelay: 2000, variant: "success" });
        },
        prepareExportData(data) {
            return data.map(item => {
                let properties = {};
                if (item.properties) {
                    if (typeof item.properties === 'string') {
                        try { properties = JSON.parse(item.properties); } catch (e) { properties = {}; }
                    } else properties = item.properties;
                }
                return {
                    'SKU': this.handleNull(item.sku, ''),
                    'Product Name': this.handleNull(item.product_name, ''),
                    'Category': this.packet.categoriesdict[item.category_id] || '',
                    'Sub Category': this.getSubcategoryLabel(item.subcategory_id) || '',
                    'Vendor': this.packet.vendorsdict[item.vendor_id] || '',
                    'Selling Price': this.formatPrice(item.selling_price),
                    'In Stock': this.handleNull(item.in_stock, 0),
                    'Available': this.handleNull(item.available, 0),
                    'Sold So Far': this.handleNull(item.sold_so_far, 0),
                    'Ordered': this.handleNull(item.ordered, 0),
                    'Scheduled': this.handleNull(item.scheduled, 0),
                    'Status': this.handleNull(item.status, ''),
                    'Discontinued': item.discontinued ? 'Yes' : 'No',
                    'Discontinued Date': item.discontinued_date ? this.formatDateTime(item.discontinued_date) : '',
                    'New Arrival': item.is_new_arrival ? 'Yes' : 'No',
                    'New Arrival Expiry': item.new_arrival_expiry_date ? this.formatDateTime(item.new_arrival_expiry_date) : '',
                    'Recently Received': item.recently_received ? 'Yes' : 'No'
                };
            });
        },
        formatPrice(price) {
            if (!price && price !== 0) return 0;
            const num = parseFloat(price);
            return isNaN(num) ? 0 : Math.round(num * 100) / 100;
        },
        handleNull(value, defaultValue = 0) {
            if (value === null || value === undefined || value === '') return defaultValue;
            return value;
        },
        calculateTotalProfit(item, period) {
            let unitsSold = 0;
            let landedCost = parseFloat(item.landed_cost) || 0;
            let sellingPrice = parseFloat(item.selling_price) || 0;
            switch (period) {
                case '7d': unitsSold = item.sold_7d || 0; break;
                case '30d': unitsSold = item.sold_30d || 0; break;
                case '60d': unitsSold = item.sold_60d || 0; break;
                case '90d': unitsSold = item.sold_90d || 0; break;
                case 'all_time': unitsSold = item.sold_so_far || 0; break;
            }
            const profitPerUnit = sellingPrice - landedCost;
            const totalProfit = profitPerUnit * unitsSold;
            return Math.round(totalProfit * 100) / 100;
        },
        hasActiveDiscount(item) {
            if (!item.special_discount_price || item.special_discount_price <= 0) return false;
            if (!item.special_discount_end_date) return false;
            const today = new Date();
            let endDate;
            if (item.special_discount_end_date.includes('-')) {
                const parts = item.special_discount_end_date.split('-');
                if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) endDate = new Date(parts[2], parts[0] - 1, parts[1]);
                else endDate = new Date(item.special_discount_end_date);
            } else endDate = new Date(item.special_discount_end_date);
            endDate.setHours(23, 59, 59, 999);
            return today <= endDate;
        },
        isDiscountExpired(item) {
            if (!item.special_discount_end_date) return true;
            const today = new Date();
            let endDate;
            if (item.special_discount_end_date.includes('-')) {
                const parts = item.special_discount_end_date.split('-');
                if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) endDate = new Date(parts[2], parts[0] - 1, parts[1]);
                else endDate = new Date(item.special_discount_end_date);
            } else endDate = new Date(item.special_discount_end_date);
            endDate.setHours(23, 59, 59, 999);
            return today > endDate;
        },
        calculateDiscountedPrice(item) {
            if (!this.hasActiveDiscount(item)) return item.selling_price;
            return parseFloat(item.special_discount_price) || item.selling_price;
        },
        formatDate(dateString) {
            if (!dateString) return '';
            if (dateString.includes('-')) {
                const parts = dateString.split('-');
                if (parts.length === 3) {
                    if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) return moment(dateString, 'MM-DD-YYYY').format('MMM Do, YYYY');
                    else if (parts[0].length === 4) return moment(dateString).format('MMM Do, YYYY');
                }
            }
            return moment(dateString).format('MMM Do, YYYY');
        },
        getProductImage(item) {
            if (!item || !item.image_url) return null;
            let imageUrl = item.image_url.trim();
            if (imageUrl === '' || imageUrl === 'null' || imageUrl === 'undefined') return null;
            if (imageUrl.includes('fmapi.myfurnituremecca.com')) return `/api/image/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
            return imageUrl;
        },
        handleImageError(event, item) {
            console.warn(`Image failed to load for ${item.sku}:`, event.target.src);
            const parent = event.target.parentElement;
            if (parent) {
                const existingFallback = parent.querySelector('.no-image-placeholder');
                if (!existingFallback) {
                    parent.innerHTML = `<div class="no-image-placeholder" style="width:60px;height:50px;display:flex;flex-direction:column;align-items:center;justify-content:center;border:1px dashed #ddd;border-radius:4px;background:#f9f9f9;"><div style="font-size:20px;margin-bottom:5px;">📷</div><div style="color:#999;font-size:10px;">No Image</div><div style="color:#ccc;font-size:8px;margin-top:2px;">${item.sku}</div></div>`;
                }
            }
            event.target.style.display = 'none';
        },
        handleImageLoad(event, item) {
            console.log(`Image loaded successfully for ${item.sku}`);
        },
        showFullImage(item) {
            if (!item) return;
            const imageUrl = this.getProductImage(item);
            if (imageUrl) {
                this.$bvModal.show('modal-image-viewer');
                this.$nextTick(() => {
                    this.currentFullSizeImage = imageUrl;
                    this.currentProductName = item.product_name || item.sku || 'Product Image';
                });
            } else {
                this.$bvToast.toast("No image available for this product", { title: "Info", autoHideDelay: 2000, variant: "info" });
            }
        },
        openImageUpload(product) {
            this.uploadProduct = product;
            this.selectedFile = null;
            this.imagePreview = null;
            this.$bvModal.show('modal-image-upload');
        },
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        handleFileSelect(event) {
            const file = event.target.files[0];
            if (!file) return;
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                this.$bvToast.toast('Please select a valid image file (JPG, PNG, GIF, WebP)', { title: "Invalid File", autoHideDelay: 2000, variant: "warning" });
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                this.$bvToast.toast('Image size should be less than 5MB', { title: "File Too Large", autoHideDelay: 2000, variant: "warning" });
                return;
            }
            this.selectedFile = file;
            const reader = new FileReader();
            reader.onload = (e) => { this.imagePreview = e.target.result; };
            reader.readAsDataURL(file);
        },
        clearImage() {
            this.selectedFile = null;
            this.imagePreview = null;
            this.$refs.fileInput.value = '';
        },
        resetImageUpload() {
            this.uploadProduct = null;
            this.selectedFile = null;
            this.imagePreview = null;
            this.uploadingImage = false;
            if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        },
        async uploadProductImage() {
            if (!this.selectedFile || !this.uploadProduct) return;
            this.uploadingImage = true;
            try {
                const base64Image = await this.fileToBase64(this.selectedFile);
                const path = "/api/product/upload_product_image/";
                const data = { product_sku: this.uploadProduct.sku, product_id: this.uploadProduct.id, image_data: base64Image };
                const response = await new Promise((resolve, reject) => { Requestor.postSyncP(path, data, this, resolve, reject); });
                if (response.data.error) throw new Error(response.data.error);
                const updatedProduct = response.data.result;
                this.updateProductInList(updatedProduct);
                this.$bvToast.toast("Product image uploaded successfully", { title: "Success", autoHideDelay: 2000, variant: "success" });
                this.$bvModal.hide('modal-image-upload');
            } catch (error) {
                console.error('Error uploading image:', error);
                this.$bvToast.toast(error.message || "Error uploading image", { title: "Error", autoHideDelay: 2000, variant: "danger" });
            } finally {
                this.uploadingImage = false;
            }
        },
        updateProductInList(updatedProduct) {
            const productIndex = this.allItems.findIndex(item => item.id === updatedProduct.id || item.sku === updatedProduct.sku);
            if (productIndex !== -1) {
                this.allItems[productIndex].image_url = updatedProduct.image_url;
                this.allItems = [...this.allItems];
                this.updateDisplayedItems();
            }
        },
        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            });
        },
        fetchSubcategories() {
            let that = this;
            let path = "/api/product/get_subcategories/";
            let data = {};
            let s = function(response) {
                if (response.data && !response.data.error) {
                    that.subcategories = response.data.result || [];
                    that.packet.subcategoriesdict = that.subcategories.reduce((map, obj) => { map[obj.id] = obj.subcategory || obj.name; return map; }, {});
                } else {
                    that.$bvToast.toast(response.data.error || "Error loading subcategories", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                }
            };
            let f = function(error) {
                console.error("Error loading subcategories:", error);
                that.$bvToast.toast("Error loading subcategories. Please check console for details.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
            };
            Requestor.postSyncP(path, data, this, s, f);
        },
        fetchSubcategory() {
            this.showCategoryStats = false;
            if (this.subcategory_id) {
                this.currentOffset = 0;
                this.hasMore = true;
                this.allItems = [];
                this.displayedItems = [];
                this.fetchProducts(0, this.pageSize, false);
            } else {
                this.currentOffset = 0;
                this.hasMore = false;
                this.allItems = [];
                this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        applyFilters() {
            if (this.itemTypeFilter !== null) this.fetch_init(false, true);
            else {
                this.currentOffset = 0;
                this.hasMore = false;
                this.allItems = [];
                this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        applyNewArrivalFilter() {
            if (this.newArrivalFilter) {
                this.currentOffset = 0;
                this.hasMore = true;
                this.allItems = [];
                this.displayedItems = [];
                this.fetchProducts(0, this.pageSize, false);
            } else {
                this.fetch_init(true);
            }
        },
        openBulkNewArrivalModal(action) {
            this.bulkNewArrivalAction = action;
            this.newArrivalDays = 30;
            this.updateChildrenNewArrival = false;
            this.newArrivalModalVisible = true;
            this.$bvModal.show('modal-bulk-new-arrival');
        },
        executeBulkNewArrivalChange() {
            if (this.selectedProducts.length === 0) {
                this.$bvToast.toast("No products selected", { 
                    title: "Warning", 
                    autoHideDelay: 1500, 
                    variant: "warning" 
                });
                return;
            }
            
            this.isLoadingMore = true;
            
            let path = "/api/product/change_bulk_product_status/";
            let data = {
                product_skus: this.selectedProducts,
                new_arrival: this.bulkNewArrivalAction,
                new_arrival_days: this.bulkNewArrivalAction ? this.newArrivalDays : 30,
                update_children: this.updateChildrenNewArrival || false,
                user_details: {
                    username: this.packet.userDetails.username,
                    user_id: this.packet.userDetails.user_id
                }
            };
            
            let that = this;
            let s = function(response) {
                that.isLoadingMore = false;
                if (response.data.error) {
                    that.$bvToast.toast(response.data.error, { 
                        title: "Error", 
                        autoHideDelay: 3000, 
                        variant: "danger" 
                    });
                } else {
                    let message = `Successfully ${that.bulkNewArrivalAction ? 'marked as new arrival' : 'removed new arrival status'} for ${response.data.result.updated_count || that.selectedProducts.length} products`;
                    if (response.data.result.skipped_count) {
                        message += ` (${response.data.result.skipped_count} skipped)`;
                    }
                    that.$bvToast.toast(message, { 
                        title: "Success", 
                        autoHideDelay: 2000, 
                        variant: "success" 
                    });
                    that.clearSelection();
                    that.showSelectionMode = false;
                    that.fetch_init(true, true);
                }
            };
            
            let f = function(error) {
                that.isLoadingMore = false;
                console.error('Error in bulk new arrival change:', error);
                that.$bvToast.toast("Error updating new arrival status", { 
                    title: "Error", 
                    autoHideDelay: 3000, 
                    variant: "danger" 
                });
            };
            
            Requestor.postAsyncP(path, data, this, s, f);
            this.$bvModal.hide('modal-bulk-new-arrival');
        },
        isNewArrivalExpiringSoon(item) {
            if (!item.new_arrival_expiry_date) return false;
            const today = new Date();
            const expiryDate = new Date(item.new_arrival_expiry_date);
            const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
            return daysLeft <= 7 && daysLeft > 0;
        },
        getDaysUntilExpiry(item) {
            if (!item.new_arrival_expiry_date) return 0;
            const today = new Date();
            const expiryDate = new Date(item.new_arrival_expiry_date);
            const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
            return Math.max(0, daysLeft);
        },
        handleResize() {
            this.isMobile = window.innerWidth < 768;
        },
        show_eta_details(item) { this.currentProduct = item; this.$bvModal.show('modal-eta-details'); },
        show_sold_details(item) { this.currentProduct = item; this.$bvModal.show('modal-sold-details'); },
        show_received_details(item) { this.currentProduct = item; this.$bvModal.show('modal-received-details'); },
        show_scheduled_details(item) { this.currentProduct = item; this.$bvModal.show('modal-scheduled-details'); },
        change_product_status(item, status) {
            let path = "/api/product/change_product_status/";
            let data = { product_sku: item.sku, status: status };
            let that = this;
            let s = function(response) {
                if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                else {
                    that.$bvToast.toast("Changed product status.", { title: "Success", autoHideDelay: 1500, variant: "success" });
                    item.status = status;
                    that.fetch_init(true, true);
                }
                that.edit_product_idx = null;
            };
            let f = function(error) {
                console.log(error);
                that.$bvToast.toast("Error changing product status.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
                that.edit_product_idx = null;
            };
            Requestor.postAsyncP(path, data, this, s, f);
        },
        update_product(item) {
            if (!item.originalValues) {
                item.originalValues = {
                    product_name: item.product_name, selling_price: item.selling_price, landed_cost: item.landed_cost,
                    fob_price: item.fob_price, wholesale_price: item.wholesale_price, category_id: item.category_id,
                    subcategory_id: item.subcategory_id, vendor_id: item.vendor_id, spiff: item.spiff, cubes: item.cubes,
                    quantity_per_box: item.quantity_per_box, special_discount_price: item.special_discount_price,
                    special_discount_end_date: item.special_discount_end_date, type_id: item.type_id, color_id: item.color_id,
                    size_id: item.size_id, material_id: item.material_id, functionality_id: item.functionality_id
                };
            }
            let product = item;
            let path = "/api/product/update_v1_with_history/";
            let data = {
                id: product.id, sku: product.sku, product_name: product.product_name, selling_price: parseFloat(product.selling_price) || 0,
                landed_cost: parseFloat(product.landed_cost) || 0, fob_price: parseFloat(product.fob_price) || 0,
                wholesale_price: parseFloat(product.wholesale_price) || 0, category_id: product.category_id,
                subcategory_id: product.subcategory_id, vendor_id: product.vendor_id, type_id: product.type_id,
                color_id: product.color_id, size_id: product.size_id, material_id: product.material_id,
                functionality_id: product.functionality_id, special_discount_price: parseFloat(product.special_discount_price) || 0,
                special_discount_end_date: product.special_discount_end_date || null,
                properties: { "spiff": parseFloat(product.spiff) || 0, "cubes": parseFloat(product.cubes) || 0, "quantity_per_box": parseInt(product.quantity_per_box) || 1 },
                original_values: item.originalValues,
                user_details: { username: this.packet.userDetails.username || "unknown", user_id: this.packet.userDetails.user_id || "unknown" }
            };
            let that = this;
            let s = function(response) {
                if (response.data.result) {
                    that.$bvToast.toast("Updated product.", { title: "Success", autoHideDelay: 1500, variant: "success" });
                    product.edit = !product.edit;
                    delete item.originalValues;
                } else {
                    that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                }
                that.edit_product_idx = null;
            };
            let f = function(error) {
                console.log(error);
                that.$bvToast.toast("Error updating product.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
                that.edit_product_idx = null;
                product.edit = !product.edit;
            };
            Requestor.postAsyncP(path, data, this, s, f);
        },
        delete_product(item) {
            let path = "/api/product/delete_v1/";
            let data = item;
            let that = this;
            let s = function(response) {
                if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                else {
                    that.$bvToast.toast("deleted product.", { title: "Success", autoHideDelay: 1500, variant: "success" });
                    that.fetch_init(true, true);
                }
                that.edit_product_idx = null;
            };
            let f = function(error) {
                console.log(error);
                that.$bvToast.toast("Error updating product.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
                that.edit_product_idx = null;
            };
            Requestor.postAsyncP(path, data, this, s, f);
        },
        sortby(key) {
            if (this.sort_asc) this.displayedItems = Requestor.sort(this.displayedItems, key);
            else this.displayedItems = Requestor.sort_reverse(this.displayedItems, key);
            this.sort_asc = !this.sort_asc;
        },
        show_item_stats(item) { this.stats_item = item; this.$bvModal.show("modal-product-stats"); },
        close_item_stats() { this.$bvModal.hide("modal-product-stats"); },
        monitor_modal_change(changed_product) {
            let item = this.allItems.filter(function(d) { d.sku == changed_product.product_sku });
            if (item.length > 0) {
                item = item[0];
                this.filtered_items = this.allItems.map(u => u.id !== item.sku ? u : item);
            }
            return;
        }
    },
    mounted() {
        this.handleResize();
        window.addEventListener("resize", this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
    },
    created() {
        this.packet = JSON.parse(localStorage.packet);
        this.packet.vendors.splice(0, 0, { value: null, text: "ALL" });
        this.packet.categories.splice(0, 0, { value: null, text: "ALL" });
        this.packet.userDetails = JSON.parse(localStorage.userDetails);
        this.typeOptions = [{ value: null, text: 'ALL TYPES' }];
        this.colorOptions = [{ value: null, text: 'ALL COLORS' }];
        this.sizeOptions = [{ value: null, text: 'ALL SIZES' }];
        this.materialOptions = [{ value: null, text: 'ALL MATERIALS' }];
        this.functionalityOptions = [{ value: null, text: 'ALL FUNCTIONALITIES' }];
        this.fetchAllHierarchyOptions();
        this.fetchSubcategories();
        this.user_location_id = this.packet.userDetails.location_id || this.packet.userDetails.location || this.packet.userDetails.locationId;
        this.locationsDict = {};
        if (this.packet.locations && Array.isArray(this.packet.locations)) {
            this.packet.locations.forEach(loc => {
                const value = loc.value || loc.id || loc.location_id;
                const text = loc.text || loc.name || loc.location_name;
                this.locationsDict[value] = text;
            });
        }
        if (this.user_location_id && this.locationsDict[this.user_location_id]) {
            this.user_location_name = this.locationsDict[this.user_location_id];
        } else {
            this.user_location_name = "Default Location";
            console.warn("User location not found in locations list:", this.user_location_id);
        }
        this.items_being_loaded = false;
        this.displayedItems = [];
        this.allItems = [];
        this.fetch_init(true);
    }
}
</script>


<style scoped>
/* COMPACT TOOLBAR STYLES */
.compact-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: #f8f9fa;
    padding: 8px 12px;
    border-radius: 8px;
    margin-bottom: 15px;
    border: 1px solid #e9ecef;
}

.toolbar-group {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    background: white;
    padding: 4px 10px;
    border-radius: 40px;
    border: 1px solid #e2e6ea;
}

.toolbar-group .btn,
.toolbar-group .b-dropdown {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    padding: 6px 12px;
    border-radius: 30px;
    font-size: 13px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.toolbar-group .btn:hover,
.toolbar-group .b-dropdown:hover {
    background: #e9ecef !important;
}

.toolbar-group .btn-primary,
.toolbar-group .btn-primary:hover {
    background: #007bff !important;
    color: white !important;
}

.toolbar-group .btn-outline-primary {
    color: #007bff;
    border: 1px solid #dee2e6 !important;
    background: white !important;
}

.toolbar-group .btn-outline-primary:hover {
    background: #007bff !important;
    color: white !important;
    border-color: #007bff !important;
}

.toolbar-group .btn-outline-success {
    color: #28a745;
    border: 1px solid #dee2e6 !important;
    background: white !important;
}

.toolbar-group .btn-outline-success:hover {
    background: #28a745 !important;
    color: white !important;
    border-color: #28a745 !important;
}

.toolbar-group .btn-outline-info {
    color: #17a2b8;
    border: 1px solid #dee2e6 !important;
    background: white !important;
}

.toolbar-group .btn-outline-info:hover {
    background: #17a2b8 !important;
    color: white !important;
    border-color: #17a2b8 !important;
}

.toolbar-group .btn-outline-warning {
    color: #ffc107;
    border: 1px solid #dee2e6 !important;
    background: white !important;
}

.toolbar-group .btn-outline-warning:hover {
    background: #ffc107 !important;
    color: #212529 !important;
    border-color: #ffc107 !important;
}

.toolbar-group .btn-outline-secondary {
    color: #6c757d;
    border: 1px solid #dee2e6 !important;
    background: white !important;
}

.toolbar-group .btn-outline-secondary:hover {
    background: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
}

.toolbar-label {
    margin-left: 6px;
    font-size: 12px;
    font-weight: 500;
}

.toolbar-info {
    font-size: 13px;
    color: #6c757d;
    background: white;
    padding: 4px 12px;
    border-radius: 30px;
    border: 1px solid #e2e6ea;
}

.mobile-filters-toggle {
    padding: 0 4px;
}

/* Responsive adjustments */
@media (max-width: 992px) {
    .compact-toolbar {
        flex-direction: column;
        align-items: stretch;
    }
    
    .toolbar-group {
        justify-content: center;
        flex-wrap: wrap;
    }
    
    .toolbar-label {
        display: none;
    }
    
    .toolbar-group .btn,
    .toolbar-group .b-dropdown {
        padding: 6px 10px;
    }
}

@media (max-width: 576px) {
    .toolbar-group {
        justify-content: space-around;
    }
    
    .toolbar-info {
        text-align: center;
        font-size: 11px;
    }
}

/* Existing filter card styles */
.filter-card {
    border: 1px solid #e8eaed;
    border-radius: 12px;
    background: #ffffff;
    overflow: hidden;
    margin-bottom: 1rem;
    transition: all 0.2s ease;
}

.filter-card:hover {
    border-color: #d0d3d8;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.filter-primary {
    background: #ffffff;
    padding: 1.25rem 1.5rem;
}

.filter-more {
    background: #fafbfc;
    border-top: 1px solid #eff2f5;
    padding: 1.25rem 1.5rem;
}

.filter-label {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #5f6b7a;
    margin-bottom: 0.5rem;
    display: block;
}

.filter-select,
.filter-input {
    border-radius: 8px;
    border: 1px solid #e2e6ea;
    background: #ffffff;
    transition: all 0.2s ease;
    font-size: 0.875rem;
    color: #2c3e50;
}

.filter-select:hover,
.filter-input:hover {
    border-color: #cbd0d6;
}

.filter-select:focus,
.filter-input:focus {
    border-color: #4a90e2;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
    outline: none;
}

.filter-search {
    border-radius: 8px;
    overflow: hidden;
}

.filter-search .form-control {
    border-right: none;
    border-radius: 8px 0 0 8px;
}

.filter-search .form-control:focus {
    box-shadow: none;
}

.filter-search .btn {
    border-radius: 0 8px 8px 0;
    background: #ffffff;
    border-left: none;
    color: #8e9aab;
    transition: all 0.2s ease;
}

.filter-search .btn:hover {
    background: #f5f7fa;
    color: #4a90e2;
}

.filter-toggle-btn {
    color: #7a8798;
    font-size: 0.75rem;
    font-weight: 500;
    text-decoration: none;
    padding: 0.375rem 0.75rem;
    border-radius: 20px;
    background: #f5f7fa;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
}

.filter-toggle-btn:hover {
    background: #eef2f7;
    color: #4a90e2;
    text-decoration: none;
}

.filter-badge {
    font-size: 0.7rem;
    padding: 0.25rem 0.6rem;
    font-weight: 500;
    border-radius: 20px;
    white-space: nowrap;
}

.reset-filters-btn {
    border-radius: 8px;
    font-size: 0.75rem;
    padding: 0.375rem 1rem;
    font-weight: 500;
    transition: all 0.2s ease;
    border: 1px solid #e2e6ea;
    background: #ffffff;
    color: #5f6b7a;
}

.reset-filters-btn:hover {
    background: #f8f9fc;
    border-color: #cbd0d6;
    color: #2c3e50;
}

.filter-active-badges {
    background: #fefce8;
    border-top: 1px solid #fef3c7;
    padding: 0.75rem 1.5rem;
    font-size: 0.75rem;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
}

.filter-active-badges small {
    color: #92400e;
    font-weight: 500;
}

.filter-badge-active {
    background: #fef3c7;
    color: #92400e;
    font-weight: 500;
    padding: 0.25rem 0.6rem;
    border-radius: 20px;
    font-size: 0.7rem;
    border: 1px solid #fde68a;
    transition: all 0.2s ease;
}

.filter-badge-active:hover {
    background: #fde68a;
}

.filter-badge-active .b-icon {
    cursor: pointer;
    opacity: 0.6;
}

.filter-badge-active .b-icon:hover {
    opacity: 1;
}

.filter-select:disabled {
    background-color: #f8f9fc;
    cursor: not-allowed;
    color: #a0aec0;
}

.row.g-2 {
    margin: 0 -0.25rem;
}

.row.g-2 > [class*="col-"] {
    padding: 0 0.25rem;
}

@media (max-width: 992px) {
    .filter-primary,
    .filter-more {
        padding: 1rem;
    }
    
    .filter-active-badges {
        padding: 0.75rem 1rem;
    }
}

@media (max-width: 768px) {
    .filter-primary,
    .filter-more {
        padding: 0.875rem;
    }
    
    .filter-label {
        font-size: 0.65rem;
        margin-bottom: 0.375rem;
    }
    
    .filter-select,
    .filter-input {
        font-size: 0.813rem;
    }
    
    .filter-toggle-btn {
        font-size: 0.7rem;
        margin-top: 0.5rem;
        display: inline-flex;
    }
    
    .reset-filters-btn {
        font-size: 0.7rem;
        width: 100%;
        margin-top: 0.5rem;
    }
    
    .filter-active-badges {
        flex-wrap: wrap;
    }
}

/* ============================================ */
/* NEW: Stats Button and Stats Row Styles */
/* ============================================ */
.filter-stats-actions {
    background: #e8f4fd;
    border-top: 1px solid #b8d4e8;
    padding: 8px 15px;
}

.stats-summary-row {
    background: #e8f4fd !important;
    border-bottom: 2px solid #4a90e2 !important;
}

.stats-summary-row td {
    background: #e8f4fd !important;
    border-color: #b8d4e8 !important;
    font-weight: 600;
    padding: 8px 5px;
}

.stats-summary-label {
    font-size: 12px;
    font-weight: 700;
    color: #0c5460;
    letter-spacing: 0.5px;
}

.stats-summary-value {
    font-size: 12px;
    font-weight: 600;
    color: #004085;
}

.stats-highlight {
    font-weight: 700;
    font-size: 13px;
}

.stats-highlight:hover {
    transform: scale(1.05);
    transition: transform 0.2s ease;
}

/* Existing table and other styles remain the same */
.image-container {
    position: relative;
    display: inline-block;
}

.image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    cursor: pointer;
    font-size: 16px;
}

.image-container:hover .image-overlay {
    opacity: 1;
}

.no-image-placeholder {
    width: 60px;
    height: 50px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 1px dashed #ddd;
    border-radius: 4px;
    background: #f9f9f9;
}

.product-image {
    transition: transform 0.2s ease;
}

.product-image:hover {
    transform: scale(1.05);
}

.srrv img {
    max-width: 100%;
    max-height: 50px;
    object-fit: contain;
}

.csv-upload-container {
    padding: 15px;
}

.csv-upload-container .alert ul {
    margin-bottom: 0;
}

.csv-upload-container .alert li {
    font-size: 0.9em;
}

.clickable-cell {
    cursor: pointer;
    transition: all 0.2s ease;
}

.clickable-cell:hover {
    background: #e4e4e4 !important;
    transform: scale(1.02);
}

.clickable-header {
    cursor: pointer;
    transition: all 0.2s ease;
}

.clickable-header:hover {
    background: #b8b8b8 !important;
}

.details-container {
    padding: 15px;
}

.diver {
    overflow: auto;
    width: 100%;
    height: 600px;
    border: 1px solid #dee2e6 !important;
}

.diver table {
    table-layout: fixed;
    width: 100%;
    border-collapse: separate !important;
    border-spacing: 0;
    border: 1px solid #495057 !important;
}

.diver table.table {
    border: 1px solid #dee2e6 !important;
}

.diver table tbody tr td,
.diver table thead tr th {
    border: 1px solid #dee2e6 !important;
    box-sizing: border-box;
}

.diver td,
.diver th {
    border: 1px solid #dee2e6 !important;
    width: 300px;
    box-sizing: border-box;
    position: relative;
}

.diver td:empty::before,
.diver th:empty::before {
    content: "\00a0";
    display: inline-block;
}

.diver td.srrv,
.diver th.srr {
    border: 1px solid #495057 !important;
}

.diver th {
    background-color: white;
    border-bottom: 2px solid #495057 !important;
}

.diver td:first-child,
.diver th:first-child {
    position: sticky;
    left: 0;
    z-index: 3;
    background-color: white;
    border: 1px solid #495057 !important;
}

.diver td:nth-child(2),
.diver th:nth-child(2) {
    position: sticky;
    left: 40px;
    z-index: 2;
    background-color: white;
    border: 1px solid #495057 !important;
}

.diver td:nth-child(3),
.diver th:nth-child(3) {
    position: sticky;
    left: 340px;
    z-index: 2;
    background-color: white;
    border: 1px solid #495057 !important;
}

.diver td:last-child.sticky-last,
.diver th:last-child.sticky-last {
    position: sticky;
    right: 0;
    z-index: 2;
    background-color: white;
    width: 150px;
    border: 1px solid #495057 !important;
}

@media (max-width: 768px) {
    td:first-child, th:first-child, td:nth-child(2), th:nth-child(2), td:nth-child(3), th:nth-child(3), td:last-child, th:last-child, thead tr th {
        position: static !important;
        left: auto !important;
        right: auto !important;
        top: auto !important;
        z-index: auto !important;
    }
}

.diver thead tr th {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: white;
    border-bottom: 2px solid #495057 !important;
}

.diver th:first-child,
.diver th.sticky-last,
.diver th:nth-child(2) {
    z-index: 2;
    background-color: white;
}

.srr {
    font-size: 13px;
    text-align: center;
    background: #ffeaa7;
    border: 1px solid #495057 !important;
    font-weight: bold;
    padding: 8px 4px;
}

.srrv {
    font-size: 12px;
    text-align: center;
    background: white;
    border: 1px solid #495057 !important;
    padding: 5px;
    contain: layout style paint;
    min-height: 40px;
    vertical-align: middle;
}

.diver tbody tr:hover td {
    background-color: #f8f9fa !important;
}

.diver tbody tr:hover td.srrv {
    background-color: #f8f9fa !important;
}

.edit {
    background: lightyellow;
    text-align: center;
    font-weight: bold;
    border: 1px solid #ffc107 !important;
}

.diver table,
.diver tr,
.diver td,
.diver th {
    border-style: solid !important;
    border-width: 1px !important;
}

.diver th[style*="background:lightgoldenrodyellow"] {
    background-color: #fef9e7 !important;
    border-color: #f1c40f !important;
}

.diver th[style*="background:lightpink"] {
    background-color: #ffd1dc !important;
    border-color: #e91e63 !important;
}

.diver th[style*="background:lightblue"] {
    background-color: #d1ecf1 !important;
    border-color: #17a2b8 !important;
}

.diver th[style*="background:lightgreen"] {
    background-color: #d1f7d1 !important;
    border-color: #28a745 !important;
}

.diver th[style*="background:lightcyan"] {
    background-color: #d1f2eb !important;
    border-color: #1abc9c !important;
}

.badge {
    border: 1px solid transparent !important;
}

.modal .table td,
.modal .table th {
    border: 1px solid #dee2e6 !important;
}

@media print {
    .diver table, .diver tr, .diver td, .diver th {
        border: 1px solid #3b3b3bff !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
}

@media (prefers-contrast: high) {
    .diver td, .diver th {
        border: 2px solid #3a3a3aff !important;
    }
}

@media (max-width: 768px) {
    td:first-child, th:first-child, td:last-child, th:last-child, .diver td:nth-child(2), .diver th:nth-child(2), thead tr th {
        position: static !important;
        left: auto !important;
        right: auto !important;
        top: auto !important;
        z-index: auto !important;
    }
    .diver {
        overflow-x: auto;
        overflow-y: auto;
    }
    .diver td.srrv, .diver th.srr {
        padding: 2px;
        font-size: 10px;
    }
}

.diver.loading {
    opacity: 0.7;
    pointer-events: none;
}

.diver td:focus,
.diver th:focus {
    outline: 2px solid #007bff;
    outline-offset: -2px;
    z-index: 3;
}

.diver .border-fallback td,
.diver .border-fallback th {
    box-shadow: inset 0 0 0 1px #dee2e6, inset 0 -1px 0 1px #dee2e6, inset -1px 0 0 1px #dee2e6 !important;
}

.discounted-price {
    font-weight: bold;
}

.input-group-text {
    background-color: #e9ecef;
    border: 1px solid #ced4da;
}

.edit .b-form-datepicker {
    background: lightyellow;
    border: 1px solid #ffc107 !important;
}

.badge-success {
    font-size: 13px;
    background-color: #28a745 !important;
}

.badge-warning {
    background-color: #eeec72 !important;
    color: #212529 !important;
    font-size: 13px;
}

.badge-secondary {
    background-color: #6c757d !important;
}

.image-upload-container {
    padding: 15px;
}

.upload-area {
    border: 2px dashed #dee2e6;
    border-radius: 10px;
    padding: 40px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background-color: #f8f9fa;
}

.upload-area:hover {
    border-color: #007bff;
    background-color: #e9ecef;
}

.upload-placeholder {
    color: #6c757d;
}

.image-preview-container {
    text-align: center;
}

.preview-image {
    max-width: 100%;
    max-height: 300px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
    object-fit: contain;
}

.preview-actions {
    display: flex;
    justify-content: center;
    gap: 10px;
}

.upload-info {
    text-align: center;
}

.barcode-image {
    transition: transform 0.2s ease;
    cursor: pointer;
}

.barcode-image:hover {
    transform: scale(1.1);
}

.no-barcode-placeholder {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 50px;
}

.barcode-hover {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    cursor: pointer;
    font-size: 16px;
}

td:has(.barcode-image) {
    position: relative;
}

td:has(.barcode-image):hover .barcode-hover {
    opacity: 1;
}

.bulk-image-upload-container {
    padding: 15px;
}

.image-preview-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 15px;
    max-height: 400px;
    overflow-y: auto;
    padding: 10px;
    border: 1px solid #dee2e6;
    border-radius: 5px;
}

.preview-item {
    text-align: center;
    padding: 10px;
    border: 1px solid #e9ecef;
    border-radius: 5px;
    background: #f8f9fa;
}

.preview-thumb {
    max-width: 100%;
    max-height: 100px;
    object-fit: contain;
    margin-bottom: 5px;
}

.preview-info {
    font-size: 12px;
}

.preview-info strong {
    display: block;
    color: #007bff;
}

.preview-info small {
    display: block;
    color: #6c757d;
    font-size: 10px;
    word-break: break-all;
}

/* PRINT BARCODE STYLES */
.print-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 15px;
    max-height: 500px;
    overflow-y: auto;
    padding: 10px;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background: #f8f9fa;
}

.print-card {
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 10px;
    text-align: center;
    transition: transform 0.2s ease;
}

.print-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.print-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.print-number {
    background: #007bff;
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
}

.print-barcode-preview {
    max-width: 100%;
    max-height: 70px;
    object-fit: contain;
    margin-bottom: 10px;
}

.print-card-text {
    font-size: 11px;
}

.print-sku {
    font-weight: bold;
    font-size: 12px;
    margin-bottom: 4px;
}

.print-name {
    color: #666;
    font-size: 10px;
    margin-bottom: 2px;
}

.print-price {
    color: #28a745;
    font-weight: bold;
    font-size: 11px;
}
</style>


// WEBPACK FOOTER //
// src/components/Products/ProductsListProducts.vue